/*
  HUNDRED cart drawer.

  DEFAULT: ON for any unpublished/development theme, OFF for the published
  one. ?hundred_cart=1 / =0 still override and are remembered per session.

  This is the replacement for the Kaching Cart drawer, built in stages so
  each one can be proven before the next. Kaching still owns the live cart
  and is not disabled, not configured, not touched. Kaching Bundles is a
  separate app and is out of scope entirely.

  TURNING IT ON
    ?hundred_cart=1   enables for this browser, remembered in sessionStorage
    ?hundred_cart=0   turns it back off
  With the flag off this file binds nothing and renders nothing, so a normal
  shopper cannot reach it.

  WHAT IT OWNS SO FAR
    stage 1  feature flag
    stage 2  normal cart rendering from /cart.js
    stage 3  quantity, remove, refresh
    stage 4  Academy visual grouping
    stage 6  free gift ladder at £80.00
    stage 7  UK free shipping ladder at £139.99

  NOT YET BUILT, still Kaching's on live: Academy safe removal, shipping
  protection, contextual upsell, upsell discounting, cart analytics, parity
  test, cutover.

  ACADEMY
    The pack arrives as SIX Shopify lines: one parent holding £129.99 and
    five children discounted to £0.00 by a line level discount titled
    "ACADEMY PACK  = More Savings". Those six lines are the backend contract
    and are never rewritten here. This groups them VISUALLY so the shopper
    sees one pack with its contents, instead of six items where five look
    free and one looks overpriced.
*/
(function () {
  'use strict';

  var FLAG = 'hundred_cart';
  var GIFT_THRESHOLD = 8000;        /* £80.00 in minor units */
  var SHIPPING_THRESHOLD = 13999;   /* £139.99, UK only */

  /*
    ============================================================
    THE THREE REVENUE MECHANICS, REVERSE ENGINEERED 2026-09-14

    Every figure below was read from Kaching's own cartSettings payload and
    then proven in a real browser against /cart.js. None of it is inferred
    from the drawer UI.

    SHIPPING PROTECTION
      product   "Shipping Protection", handle kaching-cart-toggle, UNLISTED
      variant   56262408962428, £3.00, no compare at price
      inventory not tracked, so it can always be added
      quantity  always 1
      added as an ordinary cart line, no discount, by
        POST /cart/add.js  {id, quantity:1, properties:{_kaching_cart:...}}
      auto enabled: it appeared unprompted on every cart tested
      it is a normal line, so it COUNTS toward both thresholds

    FREE GIFT
      product   "Goalkeeper Workout Plan", handle goalkeeper-workout-plan
      variant   53598862836092, price £0.00
      There is NO discount anywhere. The product simply costs nothing, so
      Kaching's "100% product-discount" config is nominal. Confirmed: the
      gift line carries no discount allocation.
      added automatically on crossing the threshold, removed automatically
      on dropping back under it. Both observed.

    THRESHOLD BASIS
      Kaching config says thresholdType "cart-total" with nothing excluded.
      Proven at the boundary: a cart of £77.98 of merchandise plus the £3.00
      protection line, total £80.98, DID unlock the gift. So the basis is
      cart.total_price including protection, not merchandise only.
      The gift is £0 so adding it cannot push a cart over the line, which is
      what stops this oscillating.

    UPSELL DISCOUNTING: THERE IS NONE
      The "£59.99 → £39.99, save 33%" on the Elite Glove Care Kit is the
      product's own Shopify compareAtPrice against its own price. Kaching
      renders it because general.useCompareAtPrice is true.
      Proven twice: added through Kaching's own upsell button, the cart line
      is £39.99 with an empty discount allocation. There is no automatic
      discount, no Function and no code behind it. Two of the three
      configured upsells even carry a 10% Kaching discount that never
      materialises in the cart at all.
      So there is no discount architecture to rebuild. Showing the real
      price against the real compare at price reproduces the economics
      exactly, and the cart and checkout already agree.
    ============================================================
  */
  var PROTECTION_VARIANT = 56262408962428;
  var GIFT_VARIANT = 53598862836092;
  var PROTECTION_PREF = 'hundred_cart_protection';
  var PROTECTION_PRICE = 300;        /* £3.00, the toggle's only variant */

  /* ---------- stage 1: the flag ----------
     Precedence, highest first:
       1  ?hundred_cart=1 / =0        an explicit choice, remembered per session
       2  the remembered choice
       3  the THEME's default, emitted by the snippet as data-hcd-default

     The theme default is 'on' for any unpublished or development theme and
     'off' for the published one, so staging needs no parameter - a phone can
     open the preview link and get the custom cart - while live stays off
     until cutover turns it on deliberately. */
  function themeDefault() {
    try {
      var root = document.querySelector('[data-hcd]');
      return !!root && root.getAttribute('data-hcd-default') === 'on';
    } catch (e) { return false; }
  }

  /*
    CUTOVER IS DONE: THIS IS THE CART, AND NOTHING STICKY CAN TURN IT OFF.

    This used to remember an explicit ?hundred_cart=0 in sessionStorage. That
    was right while live and staging were being compared side by side, and
    wrong the moment this became the only cart: any browser that had ever been
    sent to ?hundred_cart=0 stayed pinned to the old app for the life of that
    tab, long after the comparison ended. The shopper sees a cart we no longer
    maintain and there is nothing on the page telling them why.

    So the remembered value is not read any more - it is actively deleted, so
    a tab that was already pinned recovers on its next page load rather than
    staying stuck until it is closed.

    ?hundred_cart=0 still works as an escape hatch, but only for the page load
    that carries it. It cannot outlive the URL that asked for it.
  */
  function enabled() {
    try { sessionStorage.removeItem(FLAG); } catch (e) {}
    try {
      if (new URLSearchParams(window.location.search).get(FLAG) === '0') return false;
    } catch (e) {}
    return true;
  }

  if (!enabled()) return;

  /*
    Kaching binds its own cart triggers and opens its drawer alongside this
    one, and its element renders above. Measured: its subtree intercepted
    every pointer event aimed at this drawer's controls.

    So while the flag is on, and ONLY while it is on, the app's drawer is
    hidden with a CSS rule scoped to an attribute this file sets. The app is
    not disabled, not uninstalled and not reconfigured, its settings are
    untouched, and a browser without the flag gets Kaching exactly as it is
    today. Turning the flag off removes the attribute and the rule stops
    applying.
  */
  document.documentElement.setAttribute('data-hundred-cart', 'on');

  var root, bodyEl, listEl, subtotalEl, ladderEl, upsellEl, protSlotEl, busy = false;
  var sheetEl, sheetTitleEl, sheetBodyEl, scrimEl, savingEl;
  var lastCart = null;
  /*
    The most recent cart whose glove tier was commercially settled. It is the
    ONLY snapshot allowed to set customer-facing totals, so a transitional
    cart can be reconciled against without ever reaching the subtotal or the
    reward bar. Narrow by design: it holds nothing back for ordinary adds,
    removals or quantity changes, only for a tier mid-swap.
  */
  var lastCommercialCart = null;
  /*
    THE NUMBER CURRENTLY ON SCREEN, in minor units, and a one-shot override
    that forces the true server total to be painted even when the glove state
    is unsettled. Checkout compares against the first and, on a mismatch,
    uses the second. See the checkout handover guard.
  */
  var paintedSubtotal = null;
  var forceTruthfulTotal = false;
  /*
    True from the moment open() reveals the drawer until the first render
    that follows lands. See the note in open().
  */
  var openPaintPending = false;
  var upsells = null, lastUpsellSeen = null, protImg = null, lastLadderSeen = null;
  /*
    Market context, read once from the theme. countryCode is Shopify's own
    localization.country.iso_code; protUnitPrice is the protection variant's
    price in the ACTIVE PRESENTMENT CURRENCY, rendered by Liquid. Neither is
    ever derived from cart.currency, and no FX is applied anywhere in here.
  */
  var countryCode = null;
  var protUnitPrice = null;
  var ACADEMY_URL = '/pages/academy-pack';
  var savedScrollY = 0;

  /*
    ============================================================
    TELEMETRY SUPPRESSED FOR THE CUTOVER RELEASE.

    This drawer emits twelve events the current live theme does not -
    hundred_cart_open, glove_offer_view, hundred_cart_reward_unlocked,
    hundred_cart_upsell_view, hundred_cart_protection_toggle,
    hundred_cart_checkout_click and the rest. They are new instrumentation
    that arrives with the new cart, and launch night is not the time to
    change what the measurement surface looks like: the migration being
    shipped is Kaching cart -> HUNDRED cart, not one analytics architecture
    to another. dataLayer is part of that surface whether or not a platform
    consumes a given name today.

    AUDITED BEFORE SUPPRESSING. Every one of these names was searched across
    assets, sections, snippets and layout for addEventListener or any other
    reference: there are NO consumers. Nothing in the theme reads them, so
    nothing functional depends on them.

    NOT AFFECTED, and deliberately so: 'hundred-cart:open'. That one is a
    FUNCTIONAL signal - the fetch wrapper dispatches it after an external
    /cart/add.js and the drawer listens for it to open. It is dispatched
    directly, never through track(), and its name uses a hyphen and colon
    rather than underscores. Suppressing analytics here cannot silence it.

    TO RE-ENABLE after launch, as a deliberate and separately measured
    change, set HUNDRED_CART_TELEMETRY to true.
    ============================================================
  */
  var HUNDRED_CART_TELEMETRY = false;

  function track(name, detail) {
    if (!HUNDRED_CART_TELEMETRY) return;
    detail = detail || {};
    detail.event = name;
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(detail);
      }
    } catch (e) {}
    try { document.dispatchEvent(new CustomEvent(name, { detail: detail })); } catch (e) {}
  }

  /*
    THE ONLY TEST FOR "IS THIS THE UK".

    Currency is not geography. Qatar, Saudi Arabia and UPS Express Zone 741
    are enabled GBP markets, so `cart.currency === 'GBP'` called them British
    and offered them free UK shipping they can never receive.

    If the attribute is missing for any reason this returns false, which
    withholds the UK reward mechanics rather than offering them to someone
    who cannot use them. Silence is the safe failure here.
  */
  function isUK() {
    return countryCode === 'GB';
  }

  function money(cents, currency) {
    var v = (cents / 100).toFixed(2);
    if (currency === 'GBP' || !currency) return '£' + v;
    return v + ' ' + currency;
  }

  /*
    Reward copy drops the pence when there are none: "£10 AWAY", not
    "£10.00 AWAY". Real pence are kept, so "£7.01 AWAY" still reads
    correctly. Prices elsewhere keep their pence; this is the one place a
    round number reads better than an exact one.
  */
  function moneyShort(cents, currency) {
    if (cents % 100 === 0 && (currency === 'GBP' || !currency)) {
      return '£' + (cents / 100);
    }
    return money(cents, currency);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function img(src, size) {
    if (!src) return '';
    return src.replace(/(\.(?:png|jpe?g|webp|gif))(\?.*)?$/i, '_' + size + 'x$1$2');
  }

  /* ---------- stage 4: recognising the Academy pack ----------
     By the shape of the cart, not by a hardcoded product id: a parent line
     that still carries money, and sibling lines discounted to zero by a
     discount whose title names the pack. If Academy ever ships a different
     component mix this keeps working. */
  function academyDiscount(item) {
    var allocs = item.line_level_discount_allocations || [];
    for (var i = 0; i < allocs.length; i++) {
      var t = (allocs[i].discount_application &&
        allocs[i].discount_application.title) || allocs[i].title || '';
      if (/academy\s*pack/i.test(t)) return true;
    }
    return false;
  }

  /*
    ============================================================
    ACADEMY IS AN ATOMIC COMMERCIAL UNIT

    VALID    exactly 1 Academy parent AND exactly 5 Academy children
    INVALID  a parent with any other number of children

    Proven: at four children the BXGY does not discount four, it releases
    entirely. The parent keeps charging £129.99 and every component reverts
    to retail. It does not restore by itself.

    Supported mutations: add the whole pack (the builder), remove the whole
    pack (REMOVE PACK). Nothing else may touch an Academy line.

    This function NEVER repairs. Guessing a missing component, re-adding a
    parent or rebuilding the six-line payload from assumptions would turn a
    detectable problem into an untraceable one. It reports, and the callers
    decline to act.
    ============================================================
  */
  /*
    ------------------------------------------------------------
    PACK OWNERSHIP: RECORDED FIRST, INFERRED ONLY AS A FALLBACK

    The builder now stamps every line of a pack with the same hidden
    `_academy_pack` token and marks the paid line `_academy_role: parent`.
    When that token is present it is the ONLY thing consulted: it says which
    lines the pack owns, and nothing else has to be guessed.

    The fallback below is the old shape-based rule, and it is kept for one
    reason only: carts that were built before the token shipped are still
    sitting in customers' browsers and must keep working. It is strictly
    weaker - it reads ownership out of which lines Shopify's BXGY chose to
    allocate the free entitlement to, and Shopify is free to allocate it to
    an identical line the customer added themselves. That is not a
    theoretical gap; it removed the wrong merchandise in testing.
    ------------------------------------------------------------
  */
  function packToken(item) {
    var props = item && item.properties;
    return (props && props._academy_pack) || null;
  }

  function cartHasPackTokens(cart) {
    return (cart.items || []).some(function (i) { return !!packToken(i); });
  }

  /*
    ============================================================
    LEGACY ACADEMY: A PACK THAT PREDATES THE TOKEN

    Carts persist, so a shopper can arrive after cutover with a pack built by
    the old builder already in their cart. Confirmed against two real paid
    orders (#114344, #114345): every component carries NO line properties at
    all, is zeroed by the Academy BXGY, and sits under a 129.99 parent whose
    own line is non-physical with an empty SKU.

    Such a pack is perfectly valid commercially - the economics are identical
    and its checkout is correct - so it is NOT blocked. What it cannot do is
    prove which lines it owns: with no token, a component is indistinguishable
    from the same product the customer added separately, and Shopify may
    merge the two into one line. That is why REMOVE PACK once took the
    shopper's own glove.

    So legacy packs render and check out, and the operations that depend on
    knowing ownership are refused rather than guessed. The route out is the
    one that always works: remove the pack and build a new one.
    ============================================================
  */
  function isLegacyAcademy(cart) {
    var items = (cart && cart.items) || [];
    var hasParent = items.some(function (i) {
      return /academy\s*pack/i.test(i.product_title || '') && i.final_line_price > 0;
    });
    return hasParent && !cartHasPackTokens(cart);
  }

  /*
    ============================================================
    WHEN A LEGACY PACK'S OWNERSHIP CANNOT BE PROVEN

    The first version of this asked whether the pack shared a VARIANT with
    another line. That was far too narrow, and a measured cart showed why:

      Clean+ Glove Wash        0.00   <- the CUSTOMER'S item, given a free slot
      Glove Towel              0.00
      Built-In Shin Pads       0.00
      Protekt - Padded Shorts  0.00
      Core Skin - Top          0.00
      Vanguard (Black/Pink)   57.99   <- the PACK'S OWN glove, left at retail
      Academy Pack           129.99

    Clean+ is not a component of that pack and shares no variant with one.
    Adding it simply gave the Academy BXGY a sixth eligible line, and Shopify
    reallocated its five free slots across the set - taking the customer's
    product into the pack and pushing the pack's glove out to full price.
    REMOVE PACK, which reads ownership from that allocation, then deleted the
    Clean+ and left the Vanguard: it took the customer's item.

    A legacy pack carries no token, so there is no way to tell from the cart
    which lines it was built from. The only provable case is a pack sitting
    ALONE: one parent and five components, six units of merchandise and
    nothing else. The moment any other merchandise is present the allocation
    can drift and ownership becomes a guess.

    Mechanic lines - protection and the gift - are excluded from the count.
    They are never eligible and never take a slot.
    ============================================================
  */
  function legacyOwnershipAmbiguous(cart) {
    if (!isLegacyAcademy(cart)) return false;
    var units = 0;
    ((cart && cart.items) || []).forEach(function (i) {
      if (i.variant_id === PROTECTION_VARIANT || i.variant_id === GIFT_VARIANT) return;
      units += i.quantity || 0;
    });
    /* One parent plus five components. Anything else is extra merchandise,
       and extra merchandise means the free slots may have moved. */
    return units !== 6;
  }

  function academyParents(cart) {
    var items = cart.items || [];
    if (cartHasPackTokens(cart)) {
      return items.filter(function (i) {
        return packToken(i) &&
          i.properties._academy_role === 'parent';
      });
    }
    return items.filter(function (i) {
      return /academy\s*pack/i.test(i.product_title || '') && i.final_line_price > 0;
    });
  }

  function academyChildren(cart) {
    var items = cart.items || [];
    if (cartHasPackTokens(cart)) {
      return items.filter(function (i) {
        return packToken(i) && i.properties._academy_role !== 'parent';
      });
    }
    return items.filter(function (i) {
      return i.final_line_price === 0 && i.original_line_price > 0 && academyDiscount(i);
    });
  }

  /*
    Validity is judged PER PACK when tokens are present, so two packs in one
    cart are two valid units rather than one unit with ten children.
  */
  function academyIntegrity(cart) {
    var parents = academyParents(cart);
    if (!parents.length) return { present: false, valid: true };

    var kids = 0;
    academyChildren(cart).forEach(function (i) { kids += i.quantity || 1; });

    var valid, detail;
    if (cartHasPackTokens(cart)) {
      var byToken = {};
      parents.forEach(function (i) {
        byToken[packToken(i)] = { parents: i.quantity || 1, children: 0 };
      });
      var orphan = false;
      academyChildren(cart).forEach(function (i) {
        var t = byToken[packToken(i)];
        if (!t) { orphan = true; return; }
        t.children += i.quantity || 1;
      });
      valid = !orphan && Object.keys(byToken).every(function (t) {
        return byToken[t].parents === 1 && byToken[t].children === 5;
      });
      detail = { mode: 'token', packs: byToken, orphanChildren: orphan };
    } else {
      valid = parents.length === 1 && kids === 5;
      detail = { mode: 'legacy' };
    }

    if (!valid) {
      try {
        console.error('[hundred-cart] ACADEMY INTEGRITY ERROR', {
          parents: parents.length, children: kids, expected: 5,
          total: cart.total_price, detail: detail,
          note: 'pack entitlement may have collapsed; no automatic repair is attempted'
        });
      } catch (e) {}
    }
    return { present: true, valid: valid, parents: parents.length, children: kids };
  }

  function group(cart) {
    var items = cart.items || [];
    var children = [], parents = [], loose = [];
    var tokened = cartHasPackTokens(cart);

    items.forEach(function (it, i) {
      if (it.variant_id === PROTECTION_VARIANT || it.variant_id === GIFT_VARIANT) {
        it.__mechanic = true;
      }
      /*
        Address lines by KEY, not by index.

        /cart/change.js accepts either. Index looked simpler and was wrong:
        Kaching mutates the same cart concurrently, auto-adding Shipping
        Protection and gift lines, so an index read when the drawer rendered
        can point at a different product by the time the shopper taps plus.
        Measured: tapping +1 on a two line cart produced three lines and a
        subtotal that did not move. The key is stable against reordering.
      */
      it.__key = it.key;
      if (it.__mechanic) return;                       /* rendered separately */
      /*
        A tokened line is owned by the pack whatever the BXGY did to its
        price. Classifying on price meant that when Shopify allocated the
        free entitlement to a customer's own identical line, that line was
        filed under the pack and the pack's own component was filed as
        loose - so REMOVE PACK deleted the wrong one.
      */
      var owned = tokened
        ? (packToken(it) && it.properties._academy_role !== 'parent')
        : (it.final_line_price === 0 && academyDiscount(it));

      if (owned) {
        /*
          Expand by quantity. Shopify merges identical variants into one
          line, so a shopper who picks the same clothing item for two of the
          three slots arrives as ONE line with quantity 2. Counting lines
          reported "3 item pack" for a five item pack and silently dropped
          two of the shopper's choices from the cart.
        */
        for (var q = 0; q < (it.quantity || 1); q++) children.push(it);
        return;
      }
      else if (tokened
        ? (packToken(it) && it.properties._academy_role === 'parent')
        : /academy\s*pack/i.test(it.product_title || '')) parents.push(it);
      else loose.push(it);
    });

    /*
      THE GLOVE BUNDLE IS GROUPED, AND ITS PARENT IS NEVER A CART LINE.

      The parent is a pricing mechanism, not something the shopper chose. It
      was rendering as an ordinary line with its own Remove and quantity
      controls, which is wrong twice over: the shopper can delete the thing
      holding the tier price, and if they do, normalisation immediately puts
      it back, so the control does nothing and looks broken.

      So the parent is pulled out of the line list and drawn as the group's
      header, with the real gloves listed beneath it. Removing a GLOVE is the
      only action, and it is what steps the tier down.
    */
    /*
      ============================================================
      THE BUNDLE MECHANIC IS NEVER CUSTOMER-FACING

      `bundleKids` was built by testing each glove for the bundle DISCOUNT
      TITLE, and the parent was then pushed into the loose list whenever that
      test found nothing:

          if (bundleParent && !bundleKids.length) rest.push(bundleParent);

      Discount allocations vanish for a moment while Shopify recomputes a
      tier. Mid-transition the kids collapsed to zero, so the parent rendered
      as an ordinary product card with quantity and Remove controls, and the
      gloves - failing the same test - fell through to `rest` at full retail.
      Between one valid tier and the next the customer was shown

          Omen (Black/White)    £57.99
          2x Pro Glove Bundle   £90.00

      which is the raw BXGY structure presented as merchandise.

      Two stable signals were available the whole time and neither depends on
      a discount: `bundles()` knows the parent VARIANT ids, `isEligibleGlove`
      knows the glove PRODUCT ids. So a parent is recognised as a mechanic
      always, every eligible glove under it is its child whatever the
      allocation currently says, and the parent can never become a line.
      ============================================================
    */
    var bundleParent = null, bundleKids = [], rest = [];
    loose.forEach(function (it) {
      var isParent = bundles().some(function (b) { return b.id === it.variant_id; });
      if (isParent) { bundleParent = it; return; }   /* mechanic, never a line */
      if (isEligibleGlove(it)) {
        for (var q = 0; q < (it.quantity || 1); q++) bundleKids.push(it);
        return;
      }
      rest.push(it);
    });

    var gloveBundle = null;
    if (bundleParent) {
      /*
        The tier the parent actually represents, and whether the cart has
        settled onto it. A mismatch is a transition in flight, not an error:
        the block stays put, keeps the tier and price Shopify is CURRENTLY
        applying - never one it has not confirmed - and says it is updating.
      */
      var parentTier = null;
      bundles().forEach(function (b) {
        if (b.id === bundleParent.variant_id) parentTier = b;
      });
      gloveBundle = {
        parent: bundleParent,
        children: bundleKids,
        tier: parentTier,
        pending: !!(parentTier && parentTier.qty !== bundleKids.length)
      };
    } else if (bundleKids.length) {
      /* No parent, so these are ordinary gloves and belong in the line list.
         Deduped: the list is expanded by quantity for display. */
      var seenGlove = {};
      bundleKids.forEach(function (g) {
        if (g.key && !seenGlove[g.key]) { seenGlove[g.key] = true; rest.push(g); }
      });
      bundleKids = [];
    }

    /*
      ============================================================
      NOTHING THE CUSTOMER PAYS FOR MAY LEAVE THIS FUNCTION UNRENDERED

      Academy lines are lifted out of the loose list during classification,
      so they exist ONLY in `parents` and `children`. Two branches here used
      to empty those lists without putting anything back - the integrity
      guard, and an early return when either list was empty - and the lines
      then appeared in neither `packs` nor `loose`. They were not hidden by
      CSS; they were never emitted. The subtotal is computed separately from
      cart.total_price, so the drawer showed

          Subtotal  £132.99

      above a list containing nothing but Shipping Protection.

      REPRODUCED: an Academy parent with no children - the shape an
      OCU-intercepted builder submit produces, since that posts the form and
      the form carries only the parent variant - hid £129.99 of paid
      merchandise behind a £132.99 subtotal.

      A pack that IS a pack is drawn as one, exactly as before. A pack that
      is not is still drawn as an Academy block, with a visible warning and
      the prices actually being charged. It is not broken apart into five
      independent products: that would imply a pricing story that is not
      true. But an unexplained £129.99 is far worse than an ugly one.
      ============================================================
    */
    var integrity = academyIntegrity(cart);
    var intact = !(integrity.present && !integrity.valid) &&
      parents.length === 1 && children.length === 5;

    var packs = [];
    if (parents.length) {
      var ambiguous = legacyOwnershipAmbiguous(cart);
      packs = parents.map(function (p) {
        return { parent: p, children: children, intact: intact, ambiguous: ambiguous };
      });
    } else if (children.length) {
      /* Components with no parent are not a pack. Show them, never drop
         them - deduped, because the list is expanded by quantity. */
      var seenKid = {};
      children.forEach(function (c) {
        if (c.key && !seenKid[c.key]) { seenKid[c.key] = true; rest.push(c); }
      });
    }

    return { packs: packs, gloveBundle: gloveBundle, loose: rest };
  }

  /* ---------- rewards: ONE active bar, never two ----------
     The control runs a single sequential track: the sentence changes as
     tiers complete and only the CURRENT objective carries a bar. Two bars
     read as two unrelated countdowns and kill the sense of a next goal. */
  function ladderHtml(cart) {
    /*
      GB ONLY.

      Both numbers below are GBP amounts: £80.00 of merchandise for the gift,
      £139.99 for free UK delivery. Neither has an agreed value in any other
      market, and only the GB delivery zone has a free rate at all - every
      other zone is a flat charge with no threshold. Comparing them against a
      presentment total made the gift unlock at 80 SEK (£4.96) and at 80 HUF
      (£0.15), and promised free shipping that checkout would not honour.

      Withheld, not converted. An international threshold is a commercial
      decision, not something to infer from an exchange rate at render time.
    */
    if (!isUK()) return '';

    var giftTotal = giftQualifyingSubtotal(cart);
    var shipTotal = shippingQualifyingSubtotal(cart);
    var cur = cart.currency;
    var giftDone = giftTotal >= GIFT_THRESHOLD;
    var shipDone = shipTotal >= SHIPPING_THRESHOLD;
    var html = '';

    /*
      NO "FREE GIFT UNLOCKED" LINE.

      It cost a full line of the first mobile viewport to restate something
      the cart already shows: the gift is listed as its own row, labelled
      FREE GIFT, further down. Free SHIPPING keeps its chip because that one
      is still an active AOV target the shopper can act on.
    */
    if (shipDone) {
      html += '<p class="hcd__chip"><span aria-hidden="true">&#10003;</span> FREE UK SHIPPING UNLOCKED</p>';
    }

    /* The one live objective, and only one. */
    var goal = null;
    if (!giftDone) {
      goal = { label: moneyShort(GIFT_THRESHOLD - giftTotal, cur) + ' AWAY FROM YOUR FREE GIFT',
               pct: (giftTotal / GIFT_THRESHOLD) * 100 };
    } else if (!shipDone) {
      goal = { label: moneyShort(SHIPPING_THRESHOLD - shipTotal, cur) + ' AWAY FROM FREE UK SHIPPING',
               pct: (shipTotal / SHIPPING_THRESHOLD) * 100 };
    }

    if (goal) {
      html += '<p class="hcd__goal">' + esc(goal.label) + '</p>' +
              '<div class="hcd__bar"><i style="width:' + Math.min(100, goal.pct).toFixed(1) + '%"></i></div>';
    }
    return html;
  }

  /* ---------- cart lines ---------- */
  function priceHtml(it, cur) {
    /* A compare-at is only a saving if it is actually higher. Built-In Shin
       Pads ships £19.99 against £21.99 and would otherwise print a negative. */
    if (it.original_line_price > it.final_line_price) {
      return '<s>' + money(it.original_line_price, cur) + '</s> ' + money(it.final_line_price, cur);
    }
    return money(it.final_line_price, cur);
  }

  function lineHtml(it, cur) {
    return '<li class="hcd__line" data-line="' + esc(it.__key) + '">' +
      '<div class="hcd__thumb">' + (it.image ? '<img src="' + esc(img(it.image, 160)) + '" alt="" loading="lazy" width="56" height="56">' : '') + '</div>' +
      '<div class="hcd__info">' +
        '<p class="hcd__name">' + esc(it.product_title) + '</p>' +
        (it.variant_title && it.variant_title !== 'Default Title'
          ? '<p class="hcd__variant">' + esc(it.variant_title) + '</p>' : '') +
        '<div class="hcd__controls">' +
          '<div class="hcd__qty">' +
            '<button type="button" data-hcd-qty="-1" aria-label="Reduce quantity">&minus;</button>' +
            '<span aria-live="polite">' + it.quantity + '</span>' +
            '<button type="button" data-hcd-qty="1" aria-label="Increase quantity">+</button>' +
          '</div>' +
          '<button type="button" class="hcd__remove" data-hcd-remove>Remove</button>' +
        '</div>' +
      '</div>' +
      '<p class="hcd__price">' + priceHtml(it, cur) + '</p>' +
    '</li>';
  }

  /* ---------- Academy: compact by default, details on demand ----------
     The pack is one product. Five full-width name rows put ~200px of
     configurator detail in front of someone who already knows what they
     built, so the default is a thumbnail strip and the names sit behind a
     disclosure. No quantity control: the six line payload has no proven
     safe increment, so the only action is to remove the whole pack. */
  /*
    ---------- Academy: all five, always ----------
    Reverted from the thumbnail strip. The pack is roughly 60% of product
    revenue and a five-item configurable purchase; a shopper who cannot see
    what they chose, at the size they chose, cannot trust the cart. Height is
    the wrong thing to optimise here, so every component gets a row.

    The customer-facing story is ONE pack containing FIVE choices. Nothing
    here mentions six lines, £0 components or the discount that makes them
    free, and nothing here touches them: the rows are read-only projections
    of cart data.

    Per row: CHANGE, which returns to that step of the builder. Not a trash
    icon, which would imply the pack can be reduced to four items. The pack
    as a whole gets EDIT PACK and REMOVE PACK.
  */
  var ACADEMY_STEP = { 'goalkeeper gloves': '1' };

  function stepForChild(child, index) {
    /* Gloves are step 1, the accessory is step 3, clothing is step 2. Typed
       from the product where the store sets it, position as the fallback. */
    var type = (child.product_type || '').toLowerCase();
    if (ACADEMY_STEP[type]) return ACADEMY_STEP[type];
    if (index === 0) return '1';
    if (index >= 4) return '3';
    return '2';
  }

  /*
    EVERY LINE THE PACK OWNS: the parent and all five components, deduped,
    because the children list is expanded by quantity for display.
  */
  function packKeys(pack) {
    var keys = [pack.parent.key], seen = {};
    seen[pack.parent.key] = true;
    pack.children.forEach(function (c) {
      if (c.key && !seen[c.key]) { seen[c.key] = true; keys.push(c.key); }
    });
    return keys;
  }

  function packHtml(pack, cur) {
    var p = pack.parent;
    var kids = pack.children;
    var broken = pack.intact === false;

    return '<li class="hcd__pack' + (broken ? ' hcd__pack--broken' : '') +
      '" data-line="' + esc(p.__key) + '">' +
      '<div class="hcd__pack-head">' +
        '<p class="hcd__pack-title">' + esc(p.product_title) + '</p>' +
        '<p class="hcd__price">' + money(p.final_line_price, cur) + '</p>' +
      '</div>' +
      /*
        The customer is told, in the cart, that the pack price is not
        applying. Checkout is already refused in this state, so saying
        nothing would leave them at a dead button with no explanation.
      */
      /*
          The old copy asserted "its items are being charged separately".
          When the pack is a lone parent line that is simply false - the
          subtotal is the pack price and nothing else is charged at all.
          Stating a money fact we have not proven is worse than saying
          little, so this describes what the shopper can see instead.
      */
      (broken
        ? '<p class="hcd__pack-warn" role="status">We couldn\'t load this pack ' +
          'correctly. Reset your cart and build it again, or contact us.</p>'
        : '') +
      /*
        Remove pack sits on the meta line rather than in a row of its own.
        It is low-frequency destructive functionality and it was costing a
        full 34px band directly above the recommendation. Still plain text,
        still secondary, no icon, no primary-button styling: it is simply no
        longer occupying a line by itself.
      */
      '<div class="hcd__pack-meta">' +
        '<span class="hcd__pack-count">' + kids.length + ' item' +
          (kids.length === 1 ? '' : 's') + '</span>' +
        /*
          A pack built before the token cannot prove which lines it owns. When
          one of its components shares a variant with another line in the
          cart, removing "the pack" could take the shopper's own item - which
          is exactly what it once did. The control is withheld in that single
          case rather than guessing, and the shopper is told why.
        */
        /*
          NO CART STATE MAY BE A DEAD END.

          This used to read "Remove items individually" - advice that is
          impossible to follow when the pack renders zero rows, which is
          exactly the state that produces it. A shopper arriving here could
          not check out (refused), could not remove the pack (withheld), and
          had no individual rows to remove. The only escape was clearing
          site data.

          Whole-cart reset is the one recovery that needs no line ownership
          at all, which is why it is safe precisely when ownership is the
          thing that failed.
        */
        /*
          Scope matters. When the pack renders its rows, "remove items
          individually" IS followable - the colliding standalone line is
          drawn separately with its own Remove, proven with a same-variant
          duplicate added both before and after the pack. Offering a
          whole-cart reset there would throw away a shopper's other items to
          solve a problem they could fix with one tap.

          The dead end is specifically ZERO rows: nothing to remove, remove-
          pack withheld, checkout refused. That is the only case that earns
          a reset.
        */
        (kids.length === 0
          ? '<button type="button" class="hcd__pack-reset" data-hcd-reset>RESET CART</button>'
          : pack.ambiguous
          ? '<span class="hcd__pack-note">Remove items individually</span>'
          : '<button type="button" class="hcd__remove"' +
            ' data-hcd-remove-pack="' + esc(JSON.stringify(packKeys(pack))) + '">Remove pack</button>') +
      '</div>' +
      (pack.ambiguous && kids.length
        ? '<p class="hcd__pack-warn" role="status">One of these items is also in ' +
          'your cart on its own, so we can\'t tell them apart. Remove the ' +
          'items you don\'t want individually.</p>'
        : '') +

      '<ul class="hcd__pack-list">' +
        kids.map(function (c) {
          var sized = c.variant_title && c.variant_title !== 'Default Title';
          return '<li class="hcd__pack-row">' +
            '<span class="hcd__pack-thumb">' +
              (c.image ? '<img src="' + esc(img(c.image, 120)) + '" alt="" loading="lazy" width="40" height="40">' : '') +
            '</span>' +
            '<span class="hcd__pack-text">' +
              '<span class="hcd__pack-name">' + esc(c.product_title) + '</span>' +
              (sized ? '<span class="hcd__pack-size">' + esc(c.variant_title) + '</span>' : '') +
            '</span>' +
            (broken
              ? '<span class="hcd__pack-price">' + money(c.final_line_price, cur) + '</span>'
              : '<button type="button" class="hcd__change"' +
                ' data-hcd-change="' + esc(c.handle) + '"' +
                ' data-hcd-change-variant="' + c.variant_id + '"' +
                ' data-hcd-change-key="' + esc(c.key) + '"' +
                ' data-hcd-change-name="' + esc(c.product_title) + '">CHANGE</button>') +
          '</li>';
        }).join('') +
      '</ul>' +

    '</li>';
  }

  function gloveBundleHtml(b, cur) {
    var p = b.parent;
    var kids = b.children;
    /*
      The heading comes from the tier the PARENT holds, not from the number
      of gloves on screen. During a transition those disagree for a moment,
      and titling the block from the glove count would have printed
      "3 Pair Bundle £150.00" - a tier name against a price Shopify has not
      applied to it. The confirmed pair is shown, with the transition stated.
    */
    var tierQty = b.tier ? b.tier.qty : kids.length;
    return '<li class="hcd__pack' + (b.pending ? ' hcd__pack--pending' : '') +
      '" data-hcd-glove-bundle>' +
      '<div class="hcd__pack-head">' +
        '<p class="hcd__pack-title">' + tierQty + ' Pair Bundle</p>' +
        '<p class="hcd__price">' + money(p.final_line_price, cur) + '</p>' +
      '</div>' +
      '<p class="hcd__pack-count">' + (b.pending
        ? 'Updating bundle&hellip;'
        : kids.length + ' pairs included, any mix of models') + '</p>' +
      '<ul class="hcd__pack-list">' +
        kids.map(function (c) {
          var sized = c.variant_title && c.variant_title !== 'Default Title';
          return '<li class="hcd__pack-row" data-line="' + esc(c.key) + '">' +
            '<span class="hcd__pack-thumb">' +
              (c.image ? '<img src="' + esc(img(c.image, 120)) + '" alt="" loading="lazy" width="40" height="40">' : '') +
            '</span>' +
            '<span class="hcd__pack-text">' +
              '<span class="hcd__pack-name">' + esc(c.product_title) + '</span>' +
              (sized ? '<span class="hcd__pack-size">Size ' + esc(c.variant_title) + '</span>' : '') +
            '</span>' +
            /* Removing a glove is the only control, and it is what steps the
               tier down. There is nothing here that can strand the parent. */
            '<button type="button" class="hcd__change" data-hcd-remove>Remove</button>' +
          '</li>';
        }).join('') +
      '</ul>' +
    '</li>';
  }

  function giftHtml(gift, cur) {
    /* A normal, lightweight cart line. No green box, no card inside a card,
       no second copy of the reward message that is already in the ladder. */
    return '<li class="hcd__giftline">' +
      '<div class="hcd__thumb">' + (gift.image ? '<img src="' + esc(img(gift.image, 160)) + '" alt="" loading="lazy" width="64" height="64">' : '') + '</div>' +
      '<div class="hcd__info">' +
        '<p class="hcd__gift-tag">FREE GIFT</p>' +
        '<p class="hcd__name">' + esc(gift.product_title) + '</p>' +
      '</div>' +
      '<p class="hcd__price hcd__price--free">Free</p>' +
    '</li>';
  }

  /* ---------- shipping protection ----------
     An add-on control, not hero content: one compact row in the footer with
     a real switch, sitting directly above the subtotal it changes. */
  /*
    WHILE THE CUSTOMER'S OWN CHANGE IS IN FLIGHT, SHOW THEIR INTENT.

    Kaching writes this same line concurrently: its /cart/add.js?kaching-cart
    call was measured returning a cart with protLines=0 in the middle of our
    own sequence. Between the customer's click and the server settling, the
    cart legitimately reports both states, and painting each one as it
    arrives is the flip-flop. Outside a toggle the cart is the only
    authority; during one, the customer is.
  */
  var protectionPending = false;

  /*
    ============================================================
    THE TOGGLE SHOWS INTENT, NOT THE LATEST SNAPSHOT

    Turning protection OFF still glitched, and the reason was that intent
    only outranked the cart DURING the toggle's own request. The moment that
    finished, renders went back to deriving the toggle from whatever the cart
    happened to contain — and Kaching re-adds the line, so the switch flipped
    back ON until our reconcile removed it again.

    Intent is the leader and the server is the follower: reconcile enforces
    the customer's choice on the cart, so the cart converging is a matter of
    time, not of truth. The visible control therefore shows what they chose.
    If the correction genuinely fails, that surfaces as an error and the last
    confirmed state is restored, rather than the switch silently drifting.
    ============================================================
  */
  var lastProtHtml = null;

  function protectionHtml(cart) {
    var on = protectionWanted();
    /* No price rather than a wrong price. If the market price could not be
       resolved the switch still works and checkout still charges correctly;
       what it must never do is print a number the cart will not honour. */
    var protUnit = protectionUnitPrice(cart);
    return '<button type="button" class="hcd__prot" data-hcd-protection' +
        ' role="switch" aria-checked="' + (on ? 'true' : 'false') + '">' +
        (protImg
          ? '<span class="hcd__prot-icon" aria-hidden="true"><img src="' + esc(protImg) + '" alt="" width="26" height="26"></span>'
          : '') +
        '<span class="hcd__prot-text">' +
          '<span class="hcd__prot-title">Shipping Protection</span>' +
          '<span class="hcd__prot-sub">Protect your order from loss or damage.</span>' +
        '</span>' +
        (protUnit !== null
          ? '<span class="hcd__prot-price">' + money(protUnit, cart.currency) + '</span>'
          : '') +
        '<span class="hcd__switch" aria-hidden="true"><i></i></span>' +
      '</button>';
  }

  /* ---------- one upsell, chosen from real catalogue data ----------
     Candidates and their prices come from Liquid, so nothing here can offer
     an unavailable product or a price the cart will not honour. The first
     eligible candidate wins; preference order is set in the snippet. */
  function upsellCandidates() {
    if (upsells !== null) return upsells;
    upsells = [];
    try {
      var node = document.querySelector('[data-hcd-upsells]');
      if (node) upsells = JSON.parse(node.textContent) || [];
    } catch (e) { upsells = []; }
    return upsells;
  }

  /*
    ============================================================
    RECOMMENDATION TREE

    One offer, chosen from what is actually in the cart. Deterministic, in
    priority order, first match wins:

      1  Protekt system      trousers in cart, top missing   -> top
                             top in cart, trousers missing    -> trousers
      2  Core Skin system    top in cart, trousers missing    -> trousers
                             trousers in cart, top missing    -> top
      3  Glove in cart       -> glove care (Boost Duo, then Elite Kit)
      4  anything else       -> nothing

    Completing a system beats a generic accessory, which is why 1 and 2 sit
    above the glove rule: a mixed cart of gloves plus Protekt trousers gets
    the Protekt top, not glove care.

    Exclusions applied to every candidate:
      - already in the cart, INCLUDING Academy's five component lines, so a
        pack that already contains the product never has it offered back
      - not rendered by Liquid, which only emits available products
      - Protekt Padded Shorts, which is not in the candidate set at all

    An accessory-only cart gets no recommendation. No offer beats a bad one.
    ============================================================
  */
  function byHandle(h) {
    var list = upsellCandidates();
    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === h) return list[i];
    }
    /* Gloves are not in the upsell pool - they have their own tier ladder -
       but a clothing basket can be offered one, and the ADD handler resolves
       the card back through here. Without this the button did nothing. */
    return gloveCandidate(h);
  }

  /*
    ============================================================
    A CLOTHING BASKET'S BIGGEST UNMET NEED IS GLOVES

    Reported from a real cart: Protekt Bundle £99.99 plus one item was
    offered a Goalkeeper Cap. For someone buying a complete padded clothing
    system with no gloves in the basket, a £24.99 hat is not the next thing
    they need - the gloves are, and they are worth twice as much.

    The glove offered is the highest-ranked available one from the same
    best-seller order the rotation ladder uses, presented as an ordinary
    sized card so the shopper picks their size before it is added. Once any
    glove is in the cart this stage goes quiet and the basket runs the
    normal care -> utility -> matchday funnel.
    ============================================================
  */
  function gloveCandidate(handle) {
    var list = gloves();
    for (var i = 0; i < list.length; i++) {
      var g = list[i];
      if (handle && g.handle !== handle) continue;
      if (!handle) continue;
      return gloveAsCandidate(g);
    }
    return null;
  }

  function gloveAsCandidate(g) {
    if (!g || !(g.variants || []).length) return null;
    return {
      id: g.variants[0].id,
      product_id: g.product_id,
      handle: g.handle,
      title: g.title,
      price: g.price,
      compare_at: 0,
      image: g.image,
      cat: 'glove',
      sized: true,
      variants: g.variants
    };
  }

  function cartHas(cart, matcher) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      if (matcher(items[i])) return true;
    }
    return false;
  }

  function hasHandle(cart, handle) {
    return cartHas(cart, function (i) { return i.handle === handle; });
  }

  /*
    ============================================================
    THE GLOVE ROTATION LADDER

    ROOT CAUSE OF "MIXED GLOVES DO NOT DISCOUNT", FOUND 2026-09-14:
    THERE ARE TWO GLOVE MECHANICS AND THIS CART WAS MERCHANDISING THE ONE
    THAT CANNOT MIX. Full write-up in docs/glove-multibuy-root-cause.md.

    A. Kaching Bundles quantity break. collectionBreaksEnabled is FALSE, so
       it counts ONE PRODUCT at a time. Two sizes of a glove combine,
       two colourways get nothing. Three bars only, so four pairs is Bar 3
       extrapolated to £160.

    B. The Pro Glove Bundle products. Ordinary products carrying the tier
       price, each with a Shopify automatic BXGY that zeroes N glove lines.
       The BXGY's eligible set is a PRODUCT LIST of the whole current range,
       so the gloves may be ANY MIX. Proven on real carts:

         2x Pro Glove Bundle + Vanguard + Omen                  £90.00
         3x Pro Glove Bundle + those + Tempest (Pink/White)      £120.00
         4x Pro Glove Bundle + those + Tempest (Black/Red)       £150.00

       Including the £150 four-pair tier, which mechanic A cannot reach.

    So nothing needs building and nothing in Kaching needs changing. The
    drawer routes the shopper to the offer the store already sells.

    RETRO-CLAIMING WORKS, and it is the most valuable thing here. Adding the
    bundle parent to a cart that ALREADY holds loose gloves applies the
    discount to them:

         2 different gloves  £115.98 -> £90.00    saves £25.98
         3 different gloves  £173.97 -> £120.00   saves £53.97
         4 different gloves  £231.96 -> £150.00   saves £81.96

    TWO RULES THE MEASUREMENTS FORCED.

    1. UPGRADING A TIER MUST REMOVE THE OLD PARENT. Adding the 3x parent to
       a cart holding the 2x parent left BOTH, and charged £213.00. The swap
       is done in one /cart/update.js so the cart is never seen with two.

    2. NO BUNDLE OFFER WHILE AN ACADEMY PACK IS PRESENT. Academy's own BXGY
       gets five free items and, with loose gloves also in the cart, it had
       already attached itself to one of them. Claiming a bundle on top
       added £90.00 and zeroed nothing: £248.97 -> £338.97. Academy carts
       therefore get no bundle offer at all, and Academy's own £0.00 glove
       never counts toward a tier.
    ============================================================
  */
  var ACADEMY_BAR = /academy\s*pack/i;
  var BUNDLE_BAR = /pro\s*glove\s*bundle|pair\s*bundle/i;

  var gloveBundles = null;
  function bundles() {
    if (gloveBundles !== null) return gloveBundles;
    gloveBundles = [];
    try {
      var n = document.querySelector('[data-hcd-glove-bundles]');
      if (n) gloveBundles = JSON.parse(n.textContent) || [];
    } catch (e) { gloveBundles = []; }
    gloveBundles.sort(function (a, b) { return a.qty - b.qty; });
    return gloveBundles;
  }

  function bundleFor(qty) {
    var list = bundles();
    for (var i = 0; i < list.length; i++) if (list[i].qty === qty) return list[i];
    return null;
  }

  function LADDER_TOP() {
    var list = bundles();
    return list.length ? list[list.length - 1].qty : 0;
  }

  /*
    ELIGIBLE GLOVES ARE IDENTIFIED BY PRODUCT ID, not by product_type.
    The set is emitted from the gloves collection, which is the same set the
    BXGY's eligible product list covers.
  */
  var eligibleIds = null;
  function isEligibleGlove(item) {
    if (eligibleIds === null) {
      eligibleIds = {};
      gloves().forEach(function (g) { eligibleIds[g.product_id] = true; });
    }
    return !!eligibleIds[item.product_id];
  }

  function titlesOn(item) {
    return (item.line_level_discount_allocations || []).map(function (a) {
      return (a.discount_application && a.discount_application.title) || a.title || '';
    }).filter(Boolean);
  }

  function hasAcademy(cart) {
    return (cart.items || []).some(function (i) {
      return ACADEMY_BAR.test(i.product_title || '') ||
             titlesOn(i).some(function (t) { return ACADEMY_BAR.test(t); });
    });
  }

  /* The bundle parent currently in the cart, if any. */
  function activeBundle(cart) {
    var list = bundles(), found = null;
    (cart.items || []).forEach(function (i) {
      list.forEach(function (b) {
        if (i.variant_id === b.id) found = b;
      });
    });
    return found;
  }

  /*
    Counting. A glove counts when it is a paid loose line OR a line already
    zeroed by a Pro Glove Bundle. A glove zeroed by Academy never counts:
    it belongs to the pack's economics, not to this ladder. Nor does the
    free gift or the protection line, neither of which is a glove.
  */
  function countedGloves(cart) {
    var qty = 0, net = 0, gross = 0, lines = [];
    (cart.items || []).forEach(function (i) {
      if (!isEligibleGlove(i)) return;
      var titles = titlesOn(i);
      if (titles.some(function (t) { return ACADEMY_BAR.test(t); })) return;
      var bundled = titles.some(function (t) { return BUNDLE_BAR.test(t); });
      if (i.final_line_price === 0 && !bundled) return;
      qty += i.quantity || 1;
      net += i.final_line_price;
      gross += i.original_line_price;
      lines.push(i);
    });
    return { qty: qty, net: net, gross: gross, lines: lines };
  }

  /*
    ============================================================
    IS THE GLOVE TIER COMMERCIALLY SETTLED?

    Structure, never elapsed time. Shopify passes through economically
    incoherent states while it swaps a tier: on a 4 -> 3 removal the cart
    briefly holds the OLD 4-pair parent at £150 AND three gloves back at
    £57.99 retail, and reports a subtotal of £326.97 for a basket whose
    settled value is £123.00. That is a real server state and it is not a
    thing any customer should be shown - it reads as "something has gone
    badly wrong", moments before the number drops by £200.

    Settled means the parent in the cart is the parent this glove count
    calls for, and the gloves it covers are actually discounted:

      1 glove, no parent                      settled
      2/3/4 gloves + matching parent + £0     settled
      3 gloves + old 4-pair parent            TRANSITIONAL
      3 gloves at retail, no parent yet       TRANSITIONAL

    Academy is excluded: it owns its own validity test, and a pack's glove
    never counts toward a tier.
    ============================================================
  */
  function isCommerciallySettledGloveState(cart) {
    if (!bundles().length) return true;
    if (hasAcademy(cart)) return true;

    var g = countedGloves(cart);
    var parent = activeBundle(cart);
    var want = g.qty ? bundleFor(Math.min(g.qty, LADDER_TOP())) : null;

    if (!want && !parent) return true;            /* no tier applies, none present */
    if (!want || !parent) return false;           /* one without the other */
    if (parent.id !== want.id) return false;      /* the previous tier, still here */

    /* The right parent is present, so the gloves it pays for must already be
       zeroed. Until they are, the subtotal is counting them twice. */
    var covered = 0;
    g.lines.forEach(function (i) {
      if (i.final_line_price === 0) covered += i.quantity || 1;
    });
    return covered >= Math.min(g.qty, want.qty);
  }

  /*
    ============================================================
    THE NORMALISATION ENGINE

    ONE deterministic function owns the bundle parent, and every mutation
    goes through it because it is called from reconcile(), which already
    runs after every add, every quantity change, every removal and on every
    open. No tier logic lives in an event handler.

    The rule is a pure function of the cart:

      Academy present        -> NO glove bundle parent, ever
      0 or 1 eligible glove  -> no parent
      2 / 3 / 4+ gloves      -> the parent for min(count, top tier)

    Applied as a SINGLE /cart/update.js. Measured: that endpoint both adds a
    variant the cart has never held and removes one in the same request, so
    a step up, a step down and a swap are all one atomic write and the cart
    is never observed in a half-changed state.

    STEP-DOWN IS THE PART THAT MATTERS. A cart left holding a 4x parent
    after a glove is removed is a fulfilment problem, not a pricing one:
    Mintsoft would receive a bundle line that does not match the gloves.
    Because the desired state is recomputed from the cart every time rather
    than tracked, removal is handled by exactly the same code as addition,
    and there is no path that leaves an orphan parent behind.
    ============================================================
  */
  function desiredBundle(cart) {
    if (hasAcademy(cart)) return null;
    var n = countedGloves(cart).qty;
    if (n < 2) return null;
    return bundleFor(Math.min(n, LADDER_TOP()));
  }

  function normaliseBundle(cart) {
    if (!bundles().length) return Promise.resolve(cart);
    var want = desiredBundle(cart);
    var updates = {}, changed = false;

    bundles().forEach(function (b) {
      var line = lineFor(cart, b.id);
      var have = line ? line.quantity : 0;
      var target = (want && b.id === want.id) ? 1 : 0;
      if (have !== target) { updates[b.id] = target; changed = true; }
    });

    if (!changed) return Promise.resolve(cart);

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('normalise failed: ' + r.status);
        return r.json();
      })
      .then(stamp)
      .then(function (fresh) {
        track('glove_bundle_normalised', {
          tier: want ? want.qty : null,
          pair_count: countedGloves(fresh).qty,
          cart_value: fresh.total_price, currency: fresh.currency
        });
        return fresh;
      })
      .catch(function (e) {
        /* Never leave a half-applied structure on screen. Re-read the
           server's own state and render that instead of ours. */
        try { console.warn('[hundred-cart] bundle normalisation failed', e); } catch (x) {}
        return fetchCart();
      });
  }

  /*
    What the module says. The NEXT action is the hero; a tier that is already
    on is a quiet confirmation, because the customer has already bought that
    one and the money is in the next step.
  */
  function ladderOffer(cart) {
    if (!bundles().length) return null;
    if (hasAcademy(cart)) return null;

    var g = countedGloves(cart);
    if (!g.qty) return null;

    var top = LADDER_TOP();
    var active = activeBundle(cart);

    if (g.qty >= top) return null;   /* ladder complete: the kit offer takes over */

    var next = bundleFor(g.qty + 1);
    if (!next) return null;

    /* From the price the cart is ACTUALLY on: the tier total when a bundle
       is applied, the glove lines themselves when it is not. */
    var basis = active ? active.price : g.net;
    var incremental = next.price - basis;
    if (incremental <= 0) return null;

    return { have: g.qty, tier: next, incremental: incremental, applied: active };
  }

  /*
    WHAT THE SHOPPER ACTUALLY SAVES.

    Summing the BXGY allocations is wrong: on a two-glove bundle they come to
    £115.98, because the discount nominally zeroes both gloves, and it
    printed "saving £115.98" on a cart that saved £25.98. The bundle parent
    is itself paid for, so the real saving is what those gloves would have
    cost against what the tier costs.

    Kaching's quantity break has no parent line, so there the allocation IS
    the saving.
  */
  function gloveSaving(cart) {
    var active = activeBundle(cart);
    if (active) {
      var gross = 0;
      (cart.items || []).forEach(function (i) {
        if (!isEligibleGlove(i)) return;
        if (!titlesOn(i).some(function (t) { return BUNDLE_BAR.test(t); })) return;
        gross += i.original_line_price;
      });
      var saved = gross - active.price;
      return saved > 0 ? saved : 0;
    }
    var total = 0;
    (cart.items || []).forEach(function (i) {
      if (!isEligibleGlove(i)) return;
      (i.line_level_discount_allocations || []).forEach(function (a) {
        var t = (a.discount_application && a.discount_application.title) || a.title || '';
        if (/^bar\s*\d/i.test(t)) total += a.amount || 0;
      });
    });
    return total;
  }

  var ORDINAL = { 2: 'a second', 3: 'a third', 4: 'a fourth' };

  /*
    ============================================================
    THE ROTATION MODULE

    Three states, all computed from cart facts:

      claim  the shopper already holds enough gloves and is paying more than
             the store's own bundle costs. One tap applies it. This is the
             highest value state in the cart and it did not exist before:
             two different gloves is £115.98 against a £90.00 offer.
      next   add another pair to reach the next tier, with a curated row of
             gloves to choose from and the real increment on every card.
      top    the best tier is on; stop selling gloves.

    Part 13 asks for a curated mini-selection rather than the whole wall, so
    the row is capped at three, ordered by the approved bestseller list and
    filtered to what is purchasable. Any eligible glove reaches the tier, so
    every card carries the same true increment.
    ============================================================
  */
  var BEST_SELLERS = [
    'vanguard-black-pink', 'omen-black-white', 'tempest-pink-white',
    'tempest-red-black', 'vanguard-black-yellow', 'vanguard-white-pink',
    'omen-white-black'
  ];

  var gloveCatalogue = null;
  function gloves() {
    if (gloveCatalogue !== null) return gloveCatalogue;
    gloveCatalogue = [];
    try {
      var node = document.querySelector('[data-hcd-gloves]');
      if (node) gloveCatalogue = JSON.parse(node.textContent) || [];
    } catch (e) { gloveCatalogue = []; }
    return gloveCatalogue;
  }

  function rankGlove(handle) {
    var i = BEST_SELLERS.indexOf(handle);
    return i === -1 ? BEST_SELLERS.length + 1 : i;
  }

  function gloveCard(g, offer, cur) {
    var sizes = g.variants || [];
    if (!sizes.length) return '';
    return '<li class="hcd__card" data-hcd-card="' + esc(g.handle) + '" data-hcd-card-offer>' +
      '<div class="hcd__card-img">' +
        (g.image ? '<img src="' + esc(g.image) + '" alt="" loading="lazy">' : '') +
      '</div>' +
      '<p class="hcd__card-name">' + esc(g.title) + '</p>' +
      '<p class="hcd__card-note">' + offer.tier.qty + ' pairs ' + money(offer.tier.price, cur) + '</p>' +
      '<label class="hcd__card-size">' +
        '<span class="hcd__vh">Size</span>' +
        '<select data-hcd-card-size>' +
          '<option value="">Size</option>' +
          sizes.map(function (v) {
            var sel = selectedSizes[g.handle] === v.id ? ' selected' : '';
            return '<option value="' + v.id + '"' + sel + '>' + esc(v.title) + '</option>';
          }).join('') +
        '</select>' +
      '</label>' +
      /* NOT born disabled. It was, and a shopper who tapped it before
         touching the size dropdown got nothing at all: a live-looking CTA
         that silently failed. It now takes them to the size instead. */
      '<button type="button" class="hcd__card-add" data-hcd-card-add>' +
        'ADD FOR ' + money(offer.incremental, cur) +
      '</button>' +
    '</li>';
  }

  function ladderHtmlBlock(cart) {
    var o = ladderOffer(cart);
    if (!o) return '';
    var cur = cart.currency;

    var ord = ORDINAL[o.tier.qty] || ('a ' + o.tier.qty + 'th');

    /* Every eligible glove, not a truncated three. The row scrolls, so the
       constraint is the shopper's patience, not the container. */
    var list = gloves().slice().sort(function (a, b) {
      return rankGlove(a.handle) - rankGlove(b.handle);
    });

    var cards = list.map(function (g) { return gloveCard(g, o, cur); })
                    .filter(Boolean).join('');
    if (!cards) return '';

    var seen = 'next:' + o.tier.qty + ':' + o.have;
    if (lastLadderSeen !== seen) {
      lastLadderSeen = seen;
      track('glove_offer_view', {
        current_pair_count: o.have, next_pair_count: o.tier.qty,
        current_tier: o.applied ? o.applied.qty : null, next_tier: o.tier.qty,
        incremental_price: o.incremental, currency: cur
      });
    }

    return '<li class="hcd__rot" data-hcd-rot>' +
      '<p class="hcd__section-head">BUILD YOUR GLOVE ROTATION</p>' +
      /* The next action is the hero. A tier already applied is a quiet note
         underneath, because that money is already committed. */
      '<p class="hcd__rot-lead">Add ' + ord + ' pair for only ' +
        '<strong>' + money(o.incremental, cur) + '</strong></p>' +
      '<p class="hcd__rot-sub">' + o.tier.qty + ' pairs for ' +
        money(o.tier.price, cur) + ' total, any mix of models.</p>' +
      (o.applied
        ? '<p class="hcd__rot-note">' + o.applied.qty + '-pair offer applied</p>'
        : '') +
      '<ul class="hcd__rail" data-hcd-rail>' + cards + '</ul>' +
    '</li>';
  }

  /*
    ============================================================
    CATEGORY MEMBERSHIP, BY PRODUCT ID

    Handles were guessed once before and did not exist - 'grip-spray' and
    'clean-glove-wash' against the real `gripcharge` and `clean` - so the
    exclusion silently never matched. Ids cannot drift.
    ============================================================
  */
  var CARE_PRODUCTS = {
    8765531422976: 1,   /* GRIP+ Spray */
    15333601116540: 1,  /* Clean+ Glove Wash */
    15498803839356: 1,  /* Glove Boost Duo */
    15498520461692: 1   /* Elite Glove Care Kit */
  };
  var UTILITY_PRODUCTS = {
    8143223619840: 1,   /* Glove Towel */
    14782873338236: 1   /* Glove Wallet */
  };
  var SHIN_PADS = {
    15136098812284: 1,  /* Built-in Shin Pads */
    15136153010556: 1   /* Mini Shin Pads */
  };
  var GRIP_SOCKS = {
    15196063007100: 1, 15177381511548: 1, 15275446305148: 1
  };
  var MATCHDAY_PRODUCTS = {
    15136098812284: 1, 15136153010556: 1,   /* shin pads */
    15162527809916: 1,                      /* Goalkeeper Cap */
    15179751653756: 1,                      /* Water Bottle */
    15196063007100: 1, 15177381511548: 1, 15275446305148: 1,  /* grip socks */
    15145555919228: 1, 15145537634684: 1,   /* finger protect tape */
    15145534488956: 1, 15145503752572: 1
  };
  var PROTEKT_SET = {
    14988476187004: 1,  /* Protekt Padded Top */
    14984942879100: 1   /* Protekt Padded Trouser */
    /* Padded Shorts is NOT a member: see NEVER_UPSELL. */
  };
  var CORESKIN_SET = {
    14988518293884: 1,  /* Core Skin Top */
    14988522422652: 1,  /* Core Skin Trousers */
    14988504301948: 1   /* Core Skin Shorts */
  };

  /*
    ============================================================
    NEVER OFFERED, AT ANY POSITION IN THE LADDER

    The Liquid pool already excludes these by omission. This is the second
    lock: a handle added to the pool by mistake still cannot be recommended,
    because the id check runs on every candidate regardless of which stage
    produced it.

    Padded Shorts carries unresolved fulfilment risk. The bundle parents are
    discount mechanics, not things a shopper picks off a card - offering one
    would add a tier price to a cart with nothing to attach it to. The gift
    card is not merchandise. The free gift and Shipping Protection are not in
    the pool at all and are additionally guarded here by variant id.
    ============================================================
  */
  var NEVER_UPSELL = {
    14988490965372: 1,  /* Protekt Padded Shorts - fulfilment risk */
    8098938978560: 1,   /* Gift card */
    14991728279932: 1,  /* Academy Pack */
    15329989853564: 1,  /* 4x Pro Glove Bundle */
    14863374156156: 1,  /* 3x Pair Bundle */
    8463579414784: 1,   /* 2x Pro Glove Bundle */
    8862975361280: 1,   /* legacy "bundle" parent */
    15738890223996: 1,  /* Core Skin Bundle */
    15738848641404: 1   /* Protekt Bundle */
  };

  /*
    THE UNIVERSAL FALLBACK POOL, in order.

    Reached only when every needs-based stage has been satisfied or excluded.
    Chosen for breadth rather than affinity: each one is broadly useful to a
    goalkeeper, in stock at render time, and cheap enough to be an easy yes.
    The ladder walks it top to bottom and takes the first entry the cart does
    not already own, so "fallback #1 is already owned" advances to #2 with no
    special case.
  */
  var FALLBACK_POOL = [
    'glove-towel',
    'glove-boost-duo',
    'built-in-shin-pads',
    'goalkeeper-cap-black-white',
    'water-bottle',
    'mini-shin-pads',
    'core-skin-top',
    'essential-t-shirt-black',
    'essential-training-shorts-black',
    'elite-glove-care-kit',
    'clean',
    'essential-training-tee-white',
    'essential-training-tee-grey',
    'core-skin-shorts',
    'core-skin-trousers',
    'protekt-padded-top',
    'protekt-padded-trouser'
  ];

  /* Benefit copy, by category. Used when accepting the offer crosses no
     reward line: the card still has to say why the product is worth adding,
     and a threshold claim that is not true is worse than no claim. */
  var BENEFIT = {
    care:     'KEEP YOUR GRIP MATCH-READY',
    utility:  'CLEAN GRIP ON MATCHDAY',
    matchday: 'COMPLETE YOUR MATCHDAY KIT',
    protekt:  'COMPLETES YOUR PROTEKT SET',
    coreskin: 'COMPLETES YOUR CORE SKIN SET',
    essential:'TRAINING-DAY ESSENTIAL',
    glove:    'BEST-SELLING GOALKEEPER GLOVE'
  };

  function cartCovers(cart, set) {
    return ((cart && cart.items) || []).some(function (i) {
      return !!set[i.product_id];
    });
  }

  /*
    ============================================================
    THE RECOMMENDATION ENGINE

    HARD COMMERCIAL RULE: every non-empty cart holding paid merchandise
    resolves to exactly ONE active offer. Not zero, not five.

    The old engine returned null the moment its preferred candidate was
    already owned - a cart with a Boost Duo in it lost COMPLETE YOUR KIT
    entirely - so the shopper reached checkout with nothing being asked of
    them. Every stage below now ADVANCES on exclusion rather than giving up.

    DECISION HIERARCHY, first match wins:

      0  GLOVE TIER LADDER   a pair at £30-£32 against a £19.99 accessory is
                             not a close call, and showing both splits the
                             decision. The ladder owns the slot outright and
                             this function returns null so nothing is drawn
                             beside it. ladderOffer() goes quiet on its own
                             once four pairs are reached, and the stages
                             below then take over.

      1  SET COMPLETION      half a Protekt or Core Skin system in the cart.
                             Completing a set the shopper has already started
                             beats introducing a new category.

      2  CARE                glove care, once, for a basket with gloves in
                             it. Boost Duo first, then the Elite Kit, then
                             Clean+. Skipped outright if ANY care product is
                             present: that was the £39.99 double-sell.

      3  UTILITY             the towel, then the wallet.

      4  MATCHDAY            shin pads, cap, bottle, socks. Shin pads are
                             treated as one need, so owning either kind
                             skips both.

      5  FALLBACK POOL       breadth, in fixed order.

      6  CATEGORY CTA        only if the cart genuinely owns everything the
                             pool can offer. Still a COMPLETE YOUR KIT block,
                             never an empty slot.

    STAGE ORDER DEPENDS ON THE BASKET. A cart with no glove affinity - two
    Protekt garments, say - gets matchday before glove care, because glove
    care for someone who has not bought gloves is a worse offer than the
    thing that goes in their bag. Gloves, an Academy pack, or any care or
    utility product already present all count as glove affinity.
    ============================================================
  */
  function gloveAffinity(cart) {
    return cartHas(cart, isEligibleGlove) ||
           group(cart).packs.length > 0 ||
           cartCovers(cart, CARE_PRODUCTS) ||
           cartCovers(cart, UTILITY_PRODUCTS);
  }

  function pickUpsell(cart) {
    /*
      Exclusion set built from the cart itself: every variant id, every
      product id and every handle present, which for an Academy cart means
      all five chosen components as well as the parent, the gift and the
      protection line. Matched on ids, never on titles.
    */
    var inCart = { variant: {}, product: {}, handle: {} };
    (cart.items || []).forEach(function (i) {
      inCart.variant[i.variant_id] = true;
      inCart.product[i.product_id] = true;
      if (i.handle) inCart.handle[i.handle] = true;
    });

    function eligible(handle) {
      var c = byHandle(handle);
      if (!c) return null;                                  /* not in the pool, or out of stock */
      if (NEVER_UPSELL[c.product_id]) return null;          /* second lock */
      if (!(c.variants || []).length) return null;          /* no sellable size */
      if (inCart.variant[c.id]) return null;
      if (c.product_id && inCart.product[c.product_id]) return null;
      if (inCart.handle[handle]) return null;
      return c;
    }

    /* 1. SET COMPLETION. */
    function stageSet() {
      var rules = [];
      if (hasHandle(cart, 'protekt-padded-trouser')) rules.push('protekt-padded-top');
      if (hasHandle(cart, 'protekt-padded-top')) rules.push('protekt-padded-trouser');
      if (hasHandle(cart, 'core-skin-top')) { rules.push('core-skin-trousers'); rules.push('core-skin-shorts'); }
      if (hasHandle(cart, 'core-skin-trousers') || hasHandle(cart, 'core-skin-shorts')) rules.push('core-skin-top');
      for (var r = 0; r < rules.length; r++) {
        var hit = eligible(rules[r]);
        if (hit) return hit;
      }
      return null;
    }

    /* 2. CARE. One care product is the whole need. */
    function stageCare() {
      if (cartCovers(cart, CARE_PRODUCTS)) return null;
      return eligible('glove-boost-duo') ||
             eligible('elite-glove-care-kit') ||
             eligible('clean') ||
             eligible('gripcharge');
    }

    /* 3. UTILITY. */
    function stageUtility() {
      if (cartCovers(cart, UTILITY_PRODUCTS)) return null;
      return eligible('glove-towel') || eligible('glove-wallet');
    }

    /* 4. MATCHDAY. Shin pads count once; two pairs is the same need twice. */
    function stageMatchday() {
      var out = null;
      if (!cartCovers(cart, SHIN_PADS)) {
        out = eligible('built-in-shin-pads') || eligible('mini-shin-pads');
        if (out) return out;
      }
      out = eligible('goalkeeper-cap-black-white') || eligible('water-bottle');
      if (out) return out;
      if (!cartCovers(cart, GRIP_SOCKS)) {
        out = eligible('grip-socks-black-white') || eligible('grip-socks');
        if (out) return out;
      }
      return null;
    }

    /* 1b. GLOVES, for a basket that has clothing but no gloves. */
    function stageGloves() {
      if (cartHas(cart, isEligibleGlove)) return null;
      var list = gloves().slice().sort(function (a, b) {
        return rankGlove(a.handle) - rankGlove(b.handle);
      });
      for (var i = 0; i < list.length; i++) {
        var g = list[i];
        if (inCart.product[g.product_id]) continue;
        var c = gloveAsCandidate(g);
        if (c) return c;
      }
      return null;
    }

    /* 5. FALLBACK POOL. */
    function stageFallback() {
      for (var i = 0; i < FALLBACK_POOL.length; i++) {
        var hit = eligible(FALLBACK_POOL[i]);
        if (hit) return hit;
      }
      return null;
    }

    /* STAGE 0 owns the slot when it speaks. */
    if (ladderOffer(cart)) return null;

    /*
      ACADEMY SKIPS SET COMPLETION.

      The pack already contains a glove, three clothing items and an
      accessory, one of which is a Protekt garment - so the pairing rule
      read "they own a Padded Top, sell them the Trousers" and offered a
      fourth garment to someone who has just filled that category three
      times over. Measured on a real tokened pack: it recommended Padded
      Trousers ahead of glove care every time.

      The pack's components still count as COVERAGE everywhere else, so
      Academy runs care -> utility -> matchday like any other glove basket.
    */
    var isAcademy = group(cart).packs.length > 0;

    var order;
    if (isAcademy) {
      order = [stageCare, stageUtility, stageMatchday, stageFallback];
    } else if (gloveAffinity(cart)) {
      order = [stageSet, stageCare, stageUtility, stageMatchday, stageFallback];
    } else {
      /*
        NEED STATES, NOT SKU FALLBACKS. A basket with clothing and no gloves
        gets the gloves next, not whatever accessory happens to be in stock.
        Set completion still comes first: someone holding one half of a
        Protekt or Core Skin set is mid-decision on that set.
      */
      order = [stageSet, stageGloves, stageCare, stageUtility, stageMatchday, stageFallback];
    }

    for (var i = 0; i < order.length; i++) {
      var hit = order[i]();
      if (hit) return hit;
    }
    return null;   /* the cart owns the entire pool; render() shows the CTA */
  }

  /*
    The variant this card would add. A single-variant product resolves to
    itself; a sized one resolves only once the shopper has chosen, because
    nothing is ever added at a size they did not pick.
  */
  function upsellVariant(u) {
    if (!u.sized) return (u.variants && u.variants[0]) || { id: u.id, price: u.price };
    var chosen = selectedSizes[u.handle];
    if (!chosen) return null;
    for (var i = 0; i < (u.variants || []).length; i++) {
      if (u.variants[i].id === chosen) return u.variants[i];
    }
    return null;   /* the chosen size sold out between renders */
  }

  /*
    REWARD-AWARE MICROCOPY.

    Claimed only when adding THIS offer at THIS price genuinely crosses the
    line, and the gift is checked first because £80 is the nearer of the two.
    Free shipping counts intended Shipping Protection, which is why the
    shipping test uses shippingQualifyingSubtotal rather than the gift's.

    Anything else gets category benefit copy. A threshold claim that is not
    true costs more trust than it buys clicks.
  */
  function upsellHook(cart, u, price) {
    /* Same gate as the ladder, for the same reason: a reward claim is only
       made where the reward exists. Outside GB the offer still renders, it
       just carries its ordinary category benefit copy instead. */
    if (isUK()) {
      var giftNow = giftQualifyingSubtotal(cart);
      if (giftNow < GIFT_THRESHOLD && (giftNow + price) >= GIFT_THRESHOLD) {
        return { text: 'ADD &amp; UNLOCK YOUR FREE GIFT', reward: true };
      }
      var shipNow = shippingQualifyingSubtotal(cart);
      if (giftNow >= GIFT_THRESHOLD &&
          shipNow < SHIPPING_THRESHOLD &&
          (shipNow + price) >= SHIPPING_THRESHOLD) {
        return { text: 'ADD &amp; UNLOCK FREE UK SHIPPING', reward: true };
      }
    }
    var b = BENEFIT[u.cat];
    return b ? { text: b, reward: false } : null;
  }

  function upsellHtml(cart) {
    if (!cart.item_count) return '';

    /* Only a cart the shopper is actually paying for earns an offer. A cart
       holding nothing but the free gift and Shipping Protection is not a
       basket to build on. */
    if (qualifyingMerchandiseSubtotal(cart) <= 0) return '';

    /* Priority 1 is the glove rotation ladder, and it is NOT rendered here:
       it belongs directly beneath the glove line it is about, so render()
       injects it into the list. That block IS the one active offer while it
       is up, which is why nothing is drawn alongside it. */
    if (ladderOffer(cart)) return '';

    var u = pickUpsell(cart);
    var cur = cart.currency;

    /*
      STAGE 6. The cart owns everything the pool can offer. Still a
      COMPLETE YOUR KIT block - the section never disappears on a paid cart -
      but pointing at the category rather than naming a product the shopper
      already has.
    */
    if (!u) {
      return '<li class="hcd__upsell-block">' +
        '<p class="hcd__section-head">COMPLETE YOUR KIT</p>' +
        '<div class="hcd__cta-row">' +
          '<p class="hcd__cta-text">Explore goalkeeper accessories</p>' +
          '<a class="hcd__btn2" href="/collections/accessories">SHOP ACCESSORIES</a>' +
        '</div>' +
      '</li>';
    }

    var v = upsellVariant(u);
    var price = v ? v.price : u.price;
    var was = u.compare_at > price ? '<s>' + money(u.compare_at, cur) + '</s>' : '';
    var hook = upsellHook(cart, u, price);

    if (lastUpsellSeen !== u.handle) {
      lastUpsellSeen = u.handle;
      track('hundred_cart_upsell_view', {
        upsell_product: u.handle, variant_id: u.id, category: u.cat,
        cart_value: cart.total_price, currency: cur
      });
    }

    /*
      THE SIZE SELECTOR IS PART OF THE CARD, not a silent default.

      The pool emits available variants only, so every option here is a size
      the store can actually ship. The chosen size lives in selectedSizes,
      keyed by handle, which survives the repaint that follows every cart
      write - otherwise the shopper's choice vanished the moment anything
      else touched the cart.
    */
    var sizeHtml = '';
    if (u.sized) {
      sizeHtml =
        '<label class="hcd__upsell-size">' +
          '<span class="hcd__vh">Size</span>' +
          '<select data-hcd-upsell-size>' +
            '<option value="">SIZE</option>' +
            (u.variants || []).map(function (vv) {
              var sel = selectedSizes[u.handle] === vv.id ? ' selected' : '';
              return '<option value="' + vv.id + '"' + sel + '>' + esc(vv.title) + '</option>';
            }).join('') +
          '</select>' +
        '</label>';
    }

    return '<li class="hcd__upsell-block" data-hcd-upsell-card="' + esc(u.handle) + '">' +
      '<p class="hcd__section-head">COMPLETE YOUR KIT</p>' +
      '<div class="hcd__upsell-row">' +
        '<div class="hcd__thumb">' + (u.image ? '<img src="' + esc(u.image) + '" alt="" loading="lazy" width="56" height="56">' : '') + '</div>' +
        '<div class="hcd__info">' +
          '<p class="hcd__name">' + esc(u.title) + '</p>' +
          '<p class="hcd__upsell-price">' + money(price, cur) + ' ' + was + '</p>' +
          sizeHtml +
          (hook
            ? '<p class="hcd__upsell-hook' + (hook.reward ? '' : ' hcd__upsell-hook--plain') + '">' + hook.text + '</p>'
            : '') +
        '</div>' +
        '<button type="button" class="hcd__add" data-hcd-upsell-add>ADD</button>' +
      '</div>' +
      '<p class="hcd__upsell-warn" data-hcd-upsell-warn hidden>CHOOSE A SIZE FIRST</p>' +
    '</li>';
  }

  /*
    ============================================================
    EVERY PAYABLE POUND MUST BE ON SCREEN

    Checks the invariant directly on the grouped output, before any of it
    reaches the DOM: every line the customer is charged for, other than the
    two mechanic lines, must appear in a pack, in the glove bundle, or in
    the loose list. Anything else is money the drawer is charging for and
    not showing.

    It reports and does not repair. A silent correction here would hide the
    very class of fault it exists to catch.
    ============================================================
  */
  function assertVisualIntegrity(cart, g) {
    var shown = {};
    g.packs.forEach(function (pk) {
      if (pk.parent && pk.parent.key) shown[pk.parent.key] = true;
      (pk.children || []).forEach(function (c) { if (c.key) shown[c.key] = true; });
    });
    if (g.gloveBundle) {
      if (g.gloveBundle.parent.key) shown[g.gloveBundle.parent.key] = true;
      g.gloveBundle.children.forEach(function (c) { if (c.key) shown[c.key] = true; });
    }
    g.loose.forEach(function (i) { if (i.key) shown[i.key] = true; });

    var hidden = [], hiddenValue = 0, visiblePaid = 0, protectionValue = 0;
    ((cart && cart.items) || []).forEach(function (i) {
      if (i.variant_id === PROTECTION_VARIANT) { protectionValue += i.final_line_price || 0; return; }
      if (i.variant_id === GIFT_VARIANT) return;
      if (!i.final_line_price) return;
      if (shown[i.key]) { visiblePaid += i.final_line_price; return; }
      hidden.push({ title: i.product_title, variant: i.variant_title,
                    key: i.key, price: i.final_line_price });
      hiddenValue += i.final_line_price;
    });

    if (hidden.length) {
      try {
        console.error('[hundred-cart] CART VISUAL INTEGRITY FAILURE', {
          hiddenPaidMerchandise: hiddenValue,
          visiblePaidMerchandise: visiblePaid,
          protectionValue: protectionValue,
          displayedSubtotal: cart.total_price,
          lines: hidden,
          note: 'merchandise the customer is charged for is not being rendered'
        });
      } catch (e) {}
    }
    return hiddenValue;
  }

  function render(cart) {
    if (cart && cart.__seq != null) {
      if (cart.__seq < renderedSeq) return;   /* stale: a newer cart is already on screen */
      renderedSeq = cart.__seq;
    }
    lastCart = cart;
    var g = group(cart);
    assertVisualIntegrity(cart, g);

    /*
      The LIST always shows the live cart - the bundle block has its own
      "Updating bundle..." state and the shopper must see their own action
      land. Only the money waits for a state that can actually exist.
    */
    var tierSettled = isCommerciallySettledGloveState(cart);
    if (tierSettled) lastCommercialCart = cart;
    var commercial = (!tierSettled && lastCommercialCart && !forceTruthfulTotal)
      ? lastCommercialCart : cart;
    if (tierSettled) root.removeAttribute('data-hcd-tier-pending');
    else root.setAttribute('data-hcd-tier-pending', '');
    var cur = cart.currency;
    var gift = lineFor(cart, GIFT_VARIANT);

    if (!cart.item_count) {
      listEl.innerHTML = '<li class="hcd__empty">Your cart is empty.</li>';
      lastListHtml = null;
      railScrollLeft = 0;
      selectedSizes = {};
      ladderEl.innerHTML = '';
      upsellEl.innerHTML = '';
      protSlotEl.innerHTML = '';
      lastProtHtml = null;
      subtotalEl.textContent = money(0, cur);
      paintedSubtotal = 0;
      root.setAttribute('data-hcd-empty', '');
      return;
    }
    root.removeAttribute('data-hcd-empty');

    /*
      THE ROTATION OFFER SITS UNDER ITS OWN GLOVE.

      Previously it was anchored to the bottom of the drawer, below a list
      that stretched to fill the panel, which produced a single glove, a wall
      of white space, and the strongest AOV mechanic in the store reading as
      a footer widget. It is now a row in the list, emitted immediately after
      the last line of the product it is about.
    */
    var offer = ladderOffer(cart);
    var offerHtml = ladderHtmlBlock(cart);
    var lastOfferIdx = -1;
    if (offer) {
      var counted = {};
      countedGloves(cart).lines.forEach(function (i) { counted[i.key] = true; });
      g.loose.forEach(function (it, i) {
        if (counted[it.key]) lastOfferIdx = i;
      });
    }

    var rows = g.packs.map(function (p) { return packHtml(p, cur); });
    if (g.gloveBundle) rows.push(gloveBundleHtml(g.gloveBundle, cur));
    g.loose.forEach(function (it, i) {
      rows.push(lineHtml(it, cur));
      if (i === lastOfferIdx) rows.push(offerHtml);
    });
    /* A glove can only be missing from the loose list if something unusual
       happened; never drop the offer silently in that case. */
    if (offerHtml && lastOfferIdx === -1) rows.push(offerHtml);

    /*
      THE RECOMMENDATION GOES ABOVE THE FREE GIFT.

      Measured at 375 on an Academy cart, fold at 560: the gift row was 97px
      sitting at 502-599 and pushed COMPLETE YOUR KIT to 599 with its ADD
      button at 684, 160px below the fold. The gift is a reward the customer
      has already earned and receives automatically; the recommendation is
      the only thing in that view that can add revenue. Giving the earned
      reward the prominent slot and the offer the hidden one was backwards.

      The gift is still shown in full, just after the offer.
    */
    var upsell = upsellHtml(cart);
    if (upsell) rows.push(upsell);
    if (gift) rows.push(giftHtml(gift, cur));

    /*
      SKIP THE REPAINT ENTIRELY WHEN NOTHING CHANGED.

      paintThenSettle renders twice by design — once from the first /cart.js
      for speed, once after reconcile — and any background write, including
      Kaching's own, triggers another pair. When the resulting markup is
      identical, replacing it destroys the rail, resets the scroll to the
      first glove and closes an open dropdown for no reason at all. Comparing
      the built markup is far cheaper than the DOM write it avoids.
    */
    var html = rows.join('');
    if (html !== lastListHtml) {
      var rail = listEl.querySelector('[data-hcd-rail]');
      if (rail) railScrollLeft = rail.scrollLeft;

      listEl.innerHTML = html;
      lastListHtml = html;

      /* A repaint that genuinely had to happen puts the shopper back where
         they were rather than at the start of the row. */
      var fresh = listEl.querySelector('[data-hcd-rail]');
      if (fresh && railScrollLeft) fresh.scrollLeft = railScrollLeft;
    }
    ladderEl.innerHTML = ladderHtml(commercial);
    upsellEl.innerHTML = '';   /* now rendered inside the list, above the gift */
    /*
      THE PROTECTION ROW ONLY MOVES ON SETTLED STATE.

      Measured with the customer opted OUT and then adding a product: six
      visible ON/OFF transitions in eight seconds. Our reconcile removes the
      line, Kaching's embed puts it back, we remove it again, and every
      intermediate snapshot was being painted. The cart genuinely holds both
      values during that exchange; only the settled one is true.

      Items, rewards and recommendations still paint from the first response,
      so the fast first paint is untouched. This single row waits, and while
      it waits it keeps showing the last confirmed state rather than
      flashing a transient one.
    */
    /*
      DOM IDENTITY IS PRESERVED. Rewriting innerHTML with identical markup
      still destroys and recreates the switch, and the thumb repaints: a
      visible flicker with no state change behind it. Compare first.
    */
    var protHtml = protectionHtml(cart);
    if (protHtml !== lastProtHtml) {
      protSlotEl.innerHTML = protHtml;
      lastProtHtml = protHtml;
    }

    /* The money the ladder actually took off, summed from the cart's own
       discount allocations and shown once, next to the number it changed.
       The lines already carry their reduced prices; this is the total, not
       a second telling of the same saving. */
    var saved = gloveSaving(cart);
    if (savingEl) {
      savingEl.innerHTML = saved > 0
        ? '<span>Glove bundle saving</span><span class="hcd__saving-value">&minus;' +
            money(saved, cur) + '</span>'
        : '';
    }

    subtotalEl.textContent = money(commercial.total_price, cur);
    paintedSubtotal = commercial.total_price;
    if (openPaintPending) {
      openPaintPending = false;
      root.removeAttribute('data-hcd-loading');
    }
    scrollCue();
  }

  /*
    MEASURED AT 375 ON AN ACADEMY CART: the recommendation renders correctly
    and in the right place, but it starts 49px below the fold and needs 222px
    of scrolling to reach. It reads as missing.

    The order is fixed by the brief and the pack cannot be collapsed, so the
    recommendation genuinely cannot fit above the fold on a tall Academy
    cart. What it can do is stop looking like the end of the cart: the body
    gets a soft fade whenever there is more content below, which is the one
    honest signal available.
  */
  function scrollCue() {
    if (!bodyEl) return;
    var more = bodyEl.scrollHeight - bodyEl.scrollTop - bodyEl.clientHeight > 8;
    if (more) root.setAttribute('data-hcd-more', '');
    else root.removeAttribute('data-hcd-more');
  }

  /*
    ============================================================
    THE SECONDARY PANEL

    Two jobs, one component: choosing another pair of a glove already in the
    cart, and changing the size of an Academy component. Both are things the
    shopper should be able to do without being navigated out of their cart.

    Variant data is fetched from /products/<handle>.js at the moment the
    sheet opens rather than baked into the page, so a size that sold out
    since the page loaded cannot be offered.
    ============================================================
  */
  var sheetState = null;

  function openSheet(title, html, state) {
    sheetState = state || null;
    sheetTitleEl.textContent = title;
    sheetBodyEl.innerHTML = html;
    sheetBodyEl.scrollTop = 0;
    scrimEl.hidden = false;
    sheetEl.hidden = false;
  }

  function closeSheet() {
    sheetEl.hidden = true;
    scrimEl.hidden = true;
    sheetState = null;
  }

  function productJson(handle) {
    return fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function sizeButtons(variants, selectedId) {
    return '<div class="hcd__sizes">' + variants.map(function (v) {
      var name = v.title && v.title !== 'Default Title' ? v.title : 'One size';
      return '<button type="button" class="hcd__size" data-hcd-size="' + v.id + '"' +
        ' aria-pressed="' + (v.id === selectedId ? 'true' : 'false') + '">' +
        esc(name) + '</button>';
    }).join('') + '</div>';
  }

  /* ---------- Academy size change ----------
     IN-CART MUTATION OF AN ACADEMY LINE IS DISABLED. The sheet below is
     read-only: it shows the current size and sends the shopper to the
     builder. Earlier measurements suggesting a single-call swap was safe did
     not survive contact with real carts, and a corrupted pack charges the
     customer retail for five components on top of the 129.99 parent.

     (The claim that once stood here - that Academy children carry no line
     properties - was true of the old builder and is no longer true: every
     pack line now carries a hidden _academy_pack token. Ownership is read
     from that token, never from variant identity.)

     CHANGE PRODUCT is deliberately NOT done in the cart. The Function does
     not care what the component is, but the builder does: the pack is one
     glove, three clothing items and one accessory, and nothing in the cart
     can enforce that. So changing the product returns to the builder, which
     owns those rules. */
  /*
    ============================================================
    WHEN AN IN-CART SIZE CHANGE IS SAFE, AND WHEN IT IS REFUSED

    Both collision states were reproduced on real carts:

      DESTINATION COLLISION. Academy holds YS, a standalone line holds YM,
      shopper changes YS -> YM. updates[YM]=1 is ALREADY satisfied by the
      standalone line sitting at quantity 1, so Shopify does nothing. Cart
      identical afterwards, Academy still YS, and the control looks broken.

      SOURCE COLLISION. Academy holds YS and a standalone line holds YS.
      BOTH carried the "ACADEMY PACK = More Savings" allocation at £0.00, so
      nothing in the cart says which line belongs to the pack: that is
      Shopify's allocation choice, not a statement of ownership. The change
      removed the shopper's standalone line.

    Neither is fixable by being cleverer about variant ids. So the sizes that
    would collide are simply not offered, and a component whose own line
    cannot be resolved unambiguously is not editable in the cart at all.

    THE BUILDER CANNOT RESTORE SELECTIONS. academy-bundle-builder.liquid has
    no localStorage, no sessionStorage and no prepopulation from the cart, so
    sending someone there means rebuilding all five choices from scratch.
    That is said plainly rather than dressed up, and the shopper's existing
    pack is never removed to get them there.
    ============================================================
  */
  function academySafety(variantId, lineKey) {
    var cart = lastCart || { items: [] };
    var sameVariant = (cart.items || []).filter(function (i) {
      return i.variant_id === variantId;
    });
    return {
      /* More than one line on the source variant: ownership is unreadable. */
      ambiguousSource: sameVariant.length > 1,
      /* Destination variants already present anywhere else in the cart. */
      taken: (cart.items || []).reduce(function (m, i) {
        if (i.key !== lineKey) m[i.variant_id] = true;
        return m;
      }, {})
    };
  }

  /*
    ============================================================
    CHANGING A SIZE REPLACES THE WHOLE PACK, IN THAT ORDER

    Mutating one child is banned and stays banned: the BXGY is all-or-
    nothing, so a cart that passes through four children releases the
    discount and a £129.99 pack becomes £250+ of retail lines, with no
    rollback.

    So nothing is mutated. A COMPLETE replacement pack is built - the four
    untouched choices plus the new size - added under its own token, and
    only once it is verified valid is the original removed.

    Proven before building it: Shopify's BXGY discounts two Academy packs in
    one cart simultaneously. Measured - pack A 1 parent/5 children/5 free,
    pack B the same, total £262.98 with protection; removing A by line key
    left exactly one valid pack at £132.99. The transient double pack is
    real and is the price of never passing through a broken one.

    ORDER, AND WHY IT IS THIS WAY ROUND
      1 add the replacement  - if this fails, nothing has been lost
      2 verify it            - 1 parent, 5 children, all £0.00
      3 remove the original  - one /cart/update.js, all six keys at once
      4 on any failure       - remove the REPLACEMENT, keep the original

    The customer is never left without a valid pack. The worst case is a
    brief second pack that is then cleaned up, not a broken first one.
    ============================================================
  */
  function replaceAcademyPack(oldPack, oldChildKey, newVariantId) {
    var parent = oldPack.parent;
    var seen = {}, kids = [];
    oldPack.children.forEach(function (c) {
      if (c.key && !seen[c.key]) { seen[c.key] = true; kids.push(c); }
    });
    if (!parent || kids.length !== 5) {
      return Promise.reject(new Error('pack is not intact; refusing to replace'));
    }

    var token = 'ap-' + Date.now().toString(36) + '-' +
      Math.random().toString(36).slice(2, 8);
    var items = [{ id: parent.variant_id, quantity: 1,
                   properties: { _academy_pack: token, _academy_role: 'parent' } }];
    kids.forEach(function (c) {
      var id = (c.key === oldChildKey) ? newVariantId : c.variant_id;
      items.push({ id: id, quantity: 1, properties: { _academy_pack: token } });
    });

    var oldKeys = [parent.key].concat(kids.map(function (c) { return c.key; }));

    function removeByKeys(keys) {
      var updates = {};
      keys.forEach(function (k) { updates[k] = 0; });
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(function (r) { return r.json(); });
    }

    internalAdd++;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('replacement pack could not be added');
        return fetchCart();
      })
      .then(function (cart) {
        /* Is the REPLACEMENT genuinely valid before anything is removed? */
        var mine = (cart.items || []).filter(function (i) {
          return (i.properties || {})._academy_pack === token;
        });
        var par = mine.filter(function (i) { return i.properties._academy_role === 'parent'; });
        var ch  = mine.filter(function (i) { return i.properties._academy_role !== 'parent'; });
        var free = ch.filter(function (i) { return i.final_line_price === 0; });
        if (par.length !== 1 || ch.length !== 5 || free.length !== 5) {
          /* Undo OUR addition, leave the shopper's original untouched. */
          return removeByKeys(mine.map(function (i) { return i.key; }))
            .then(function () {
              throw new Error('replacement did not price correctly; original kept');
            });
        }
        return removeByKeys(oldKeys);
      })
      .then(function (cart) {
        setTimeout(function () { internalAdd = Math.max(0, internalAdd - 1); }, 1500);
        return cart ? stamp(cart) : fetchCart();
      })
      .catch(function (err) {
        setTimeout(function () { internalAdd = Math.max(0, internalAdd - 1); }, 1500);
        throw err;
      });
  }

  function openAcademySize(handle, variantId, lineKey, name) {
    /*
      PICK A SIZE, SAVE, AND THE WHOLE PACK IS REPLACED.

      The sheet writes to the cart again, but never by mutating a child. The
      current size is shown disabled, the others are selectable, and SAVE
      CHANGE goes through replaceAcademyPack(): a complete replacement pack
      is added and verified before the original is removed. See the note
      above that function for the ordering and why it cannot be reversed.
    */
    productJson(handle).then(function (p) {
      var avail = (p.variants || []).filter(function (v) { return v.available; });
      var real = avail.filter(function (v) { return v.title && v.title !== 'Default Title'; });
      var current = null;
      real.forEach(function (v) { if (v.id === variantId) current = v.title; });

      var html =
        '<p class="hcd__sheet-note" style="padding-block-start:16px">' +
          esc(name) + (current ? ', size ' + esc(current) : '') + '</p>';

      if (real.length > 1) {
        html += '<div class="hcd__sizes">' + real.map(function (v) {
          return '<button type="button" class="hcd__size"' +
            ' data-hcd-size="' + v.id + '"' +
            (v.id === variantId ? ' disabled' : '') +
            ' aria-pressed="' + (v.id === variantId ? 'true' : 'false') + '">' +
            esc(v.title) + '</button>';
        }).join('') + '</div>';
        html += '<button type="button" class="hcd__btn2" data-hcd-size-save disabled>' +
          'SAVE CHANGE</button>';
        html += '<p class="hcd__sheet-note" data-hcd-size-error hidden></p>';
      }

      html += '<div class="hcd__sheet-alt">' +
        '<p class="hcd__sheet-note">Changing a different item is done in the ' +
          'pack builder.</p>' +
        '<a class="hcd__btn2" href="' + ACADEMY_URL + '">OPEN THE PACK BUILDER</a>' +
      '</div>';

      openSheet('Change size', html, { kind: 'academy', from: variantId, fromKey: lineKey, selected: 0 });
    }).catch(function () { closeSheet(); });
  }

  /*
    ADDING A PAIR AT A TIER, AND CLAIMING ONE.

    Both go through /cart/update.js as a SINGLE request, because the cart
    must never be observed between steps:

      claim    set the bundle parent to 1. The gloves are already there and
               the BXGY zeroes them.
      add      set the bundle parent to 1, set the chosen glove to its
               current quantity plus one, and where the shopper is moving up
               a tier, set the OLD parent to 0 in the same call. Adding the
               3x parent without removing the 2x left both in the cart and
               charged £213.00.
  */
  function applyTier(tierQty, gloveVariantId) {
    var target = bundleFor(tierQty);
    if (!target) return Promise.resolve();

    return fetchCart().then(function (cart) {
      var stale = bundles().filter(function (b) {
        return b.id !== target.id && lineFor(cart, b.id);
      });
      var haveTarget = !!lineFor(cart, target.id);

      /*
        NO OLD PARENT TO REMOVE -> PLAIN ADD.

        /cart/update.js takes absolute quantities keyed by variant, and a key
        naming a variant that is not in the cart is ignored. Claiming a tier
        is exactly that case: the only change is a parent the cart has never
        seen, so an update call did nothing at all and the subtotal never
        moved. add.js is the right primitive here and adds both lines in one
        request.
      */
      if (!stale.length) {
        var items = [];
        if (!haveTarget) items.push({ id: target.id, quantity: 1 });
        if (gloveVariantId) items.push({ id: gloveVariantId, quantity: 1 });
        if (!items.length) return null;
        internalAdd++;
        setTimeout(function () { internalAdd = Math.max(0, internalAdd - 1); }, 3000);
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        });
      }

      /*
        MOVING UP A TIER -> ONE ATOMIC UPDATE.

        Adding the 3x parent to a cart holding the 2x parent left both and
        charged £213.00, so the removal and the addition have to land in the
        same request. Here update.js is correct precisely because it is
        also changing a line that already exists.
      */
      var updates = {};
      stale.forEach(function (b) { updates[b.id] = 0; });
      updates[target.id] = 1;
      if (gloveVariantId) {
        var existing = lineFor(cart, gloveVariantId);
        updates[gloveVariantId] = (existing ? existing.quantity : 0) + 1;
      }
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      });
    });
  }

  /*
    ============================================================
    FAST FIRST PAINT

    MEASURED BEFORE THIS EXISTED, at 375 on a real add:

      +  0ms  click
      +674ms  /cart/add.js responds
      +938ms  drawer opens, showing nothing
      +3032ms first render of the cart lines

    2.1 seconds of an open, empty drawer. The cause was that NOTHING was
    painted until reconcile() finished, and reconcile is recursive: every
    correction it makes does mutate -> refetch -> reconcile again. Shipping
    Protection is auto-added to essentially every cart, so that slow path was
    the normal path, not an edge case, and Kaching's own writes were landing
    in the middle of it.

    So the cart is painted from the FIRST /cart.js response and reconcile
    runs afterwards, repainting when it has finished. The shopper sees what
    they just added straight away; protection, the gift line, tier
    normalisation and the recommendation settle a moment later. Nothing is
    guessed: the first paint is still the server's own cart, just an earlier
    one.
  */
  /*
    ============================================================
    ONE SETTLE LIFECYCLE AT A TIME

    THE LOOP, EXACTLY:

      our reconcile writes
        -> Kaching reacts with its own /cart/add.js
          -> our fetch hook sees it
            -> paintThenSettle
              -> reconcile
                -> writes
                  -> Kaching reacts ... and round again.

    Each turn AMPLIFIED rather than damped, which is why the heaviest cart
    was the worst: Academy reached 40 requests and 13.5s once reconcile got
    faster at issuing writes. The counts varied run to run because the loop
    length depends on timing, not on the action.

    External changes are not ignored. While a settle is running, anything
    Kaching does marks the cart DIRTY. The running settle finishes, and then
    AT MOST ONE further pass runs, and only if the cart still violates an
    invariant. A dirty flag cannot schedule a third pass.
    ============================================================
  */
  var settling = false;
  var dirty = false;

  /*
    OUR WRITES GO ONE AT A TIME.

    Reconcile, a customer action and a bundle normalisation could all be
    writing at once, and each write provokes a Kaching response, so racing
    writes multiplied the traffic. This is a queue for OUR writes only: it
    sequences them, it does not make the UI wait, because rendering happens
    off the mutation response before the queue drains.
  */
  var writeChain = Promise.resolve();
  /*
    ============================================================
    CHECKOUT IS A TERMINAL STATE FOR OUR OWN WRITES

    From the moment the customer clicks CHECKOUT, nothing this file does in
    the background may alter protection, the gift, the glove tier, Academy or
    any quantity. The final protection decision has to be the LAST word we
    speak, not merely the most recent one at the time we spoke it.

    The race this closes is ours: reconcile() reaches the cart through the
    fetch hook's deferred settle, which is scheduled 250ms after ANY
    /cart/add.js response. A settle scheduled before the click - or started
    during the enforce - would otherwise land after the final check and write
    to the cart on its way out the door. `busy` and `protectionPending` were
    sampled once before the enforce, which does not cover anything scheduled
    afterwards.

    It does NOT close the other race, and cannot: Kaching Cart owns the same
    protection variant and writes it from outside this file. That is an
    architectural conflict, resolved by disabling Kaching Cart at cutover and
    documented in docs/cutover-dependency.md - not by fighting it here.

    enforceProtectionIntent() is deliberately NOT gated: it is the checkout
    flow's own correction and must still be able to write.
    ============================================================
  */
  var checkoutFinalizing = false;

  function backgroundWritesBlocked() {
    return checkoutFinalizing;
  }

  function queueWrite(fn) {
    var run = writeChain.then(fn, fn);
    writeChain = run.then(function () {}, function () {});
    return run;
  }

  function paintThenSettle(cartPromise) {
    if (backgroundWritesBlocked()) {
      /* Checkout owns the cart now. Paint what arrived, write nothing. */
      return cartPromise.then(function (cart) {
        if (cart) render(cart);
        return cart;
      });
    }
    if (settling) {
      /* A lifecycle is already running and will observe whatever changed. */
      dirty = true;
      return cartPromise;
    }
    settling = true;

    return cartPromise
      .then(function (cart) {
        if (cart) render(cart);          /* real server state, immediately */
        return reconcile(cart);
      })
      .then(function (cart) {
        /* Exactly one catch-up pass for anything that landed mid-settle. */
        if (!dirty) return cart;
        dirty = false;
        return fetchCart().then(reconcile);
      })
      .then(function (cart) {
        if (cart) { cart.__settled = true; render(cart); }
        return cart;
      })
      .catch(function () {
        return fetchCart().then(function (c) {
          if (c) { c.__settled = true; render(c); }
          return c;
        }).catch(function () {});
      })
      .then(function (c) {
        settling = false;
        dirty = false;
        return c;
      });
  }

  /*
    ============================================================
    CART LINE IDENTITY: THE RULE THIS CART KEEPS BREAKING

    Hit four times now (Remove pack, CHANGE, and two handler bindings), so
    it is written down rather than remembered:

      EXISTING CART LINE   ->  target it by LINE KEY.
      PRODUCT / VARIANT ID ->  identifies MERCHANDISE, not a unique line.
      NEW ADDITION         ->  a variant id is acceptable only where creating
                               a new item is semantically right AND the
                               collision behaviour has been proven.
      ACADEMY              ->  never infer bundle-line ownership from variant
                               identity. The same variant can sit inside the
                               pack and outside it at the same time, and the
                               discount allocation is Shopify's choice, not a
                               statement of ownership.

    Where identity is ambiguous the correct behaviour is to REFUSE the
    mutation and offer a safe path, not to guess which line was meant.
    ============================================================
  */

  /*
    ============================================================
    LOADING FEEDBACK IS LOCAL TO THE CONTROL THAT IS WORKING

    The drawer had ONE global signal:

        .hcd[data-hcd-busy] .hcd__body { opacity: .55; pointer-events: none }

    set and cleared from six places, across a reconcile that makes up to
    seven corrective passes. So a single click dimmed the entire cart to 55%
    and back, repeatedly — the pulsing — and background housekeeping like
    gift reconciliation did it with no user action at all.

    `busy` remains as a JS guard against concurrent writes, but it no longer
    paints anything. The control the customer actually touched shows its own
    pending state, and secondary work shows nothing.
    ============================================================
  */
  function markPending(el, on) {
    if (!el) return;
    if (on) {
      el.setAttribute('data-hcd-pending', '');
      if ('disabled' in el) el.disabled = true;
    } else {
      el.removeAttribute('data-hcd-pending');
      if ('disabled' in el) el.disabled = false;
    }
  }

  function markRowPending(el, on) {
    var row = el && el.closest('[data-line], .hcd__pack-row, .hcd__line');
    if (!row) return;
    if (on) row.setAttribute('data-hcd-removing', '');
    else row.removeAttribute('data-hcd-removing');
  }

  /* ---------- stage 3: mutations ---------- */
  /*
    ============================================================
    RENDER GENERATIONS: NEWER CART STATE IS NEVER OVERWRITTEN BY OLDER

    Measured during one Academy add with the drawer open:

      +3238ms  REQ#12 /cart.js      started FIRST
      +3243ms  REQ#13 /cart.js
      +3429ms  RES#13               completed FIRST
      +3474ms  RES#12               older request, would paint LAST

    Two overlapping reads finishing out of order, and their payloads
    genuinely disagree: protection line count across that single add went
    0, 1, 1, 0, 1, 0, 1 because Kaching writes the same line concurrently
    (seen as /cart/add.js?kaching-cart=true returning protLines=0). An older
    snapshot repainting over a newer one is exactly how a toggle flips by
    itself with nobody touching it.

    Every cart object is stamped with a monotonic sequence at the moment it
    is received, and render() drops anything older than what is already on
    screen. This costs nothing and cannot regress the fast first paint: the
    first response still paints, it simply cannot be undone by a straggler.
    ============================================================
  */
  var cartSeq = 0, renderedSeq = -1;

  /*
    ============================================================
    LOCAL UI STATE LIVES OUTSIDE THE DOM

    The rotation rail is inside listEl, and render() replaces listEl wholesale.
    Everything the shopper was doing in there — how far along the row they had
    scrolled, which size they had picked on which card — was stored only in
    markup that was about to be destroyed. Any repaint sent them back to the
    first glove with an empty dropdown.

    There are two kinds of state here and they are not the same thing:

      SERVER  cart contents, glove count, active tier, offer economics
      LOCAL   rail scroll position, selected size per product

    Local state is kept here, keyed by product handle, and survives any
    number of repaints. Choosing a size is NOT a cart mutation and makes no
    request; it only writes to this map.
    ============================================================
  */
  var selectedSizes = {};   /* handle -> variant id */
  var railScrollLeft = 0;
  var lastListHtml = null;

  function stamp(cart) {
    if (cart && typeof cart === 'object') cart.__seq = ++cartSeq;
    return cart;
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(stamp)
      .then(observeMerchandise);
  }

  function change(key, quantity) {
    if (busy) return Promise.resolve();
    busy = true;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(stamp)
      .then(function (cart) {
        /* change.js returns the new cart, so the removal or quantity is on
           screen before reconcile decides what else has to move. */
        render(cart);
        return reconcile(cart);
      })
      .then(function (cart) {
        render(cart);
        track('hundred_cart_quantity_changed', {
          cart_value: cart.total_price, currency: cart.currency
        });
        document.dispatchEvent(new CustomEvent('hundred:cart:updated', { detail: { cart: cart } }));
      })
      .catch(function () { return fetchCart().then(render); })
      .then(function () {
        busy = false;
      });
  }

  /* ---------- mechanic helpers ----------
     Everything is keyed on VARIANT ID, not on a line property. Kaching adds
     these same two lines with its own _kaching_cart property, and while the
     flag is on both this code and Kaching's can be running. Matching on the
     variant means we recognise its lines as ours and can never end up with
     two protection lines or two gifts. */
  function linesFor(cart, variantId) {
    return ((cart && cart.items) || []).filter(function (i) {
      return i.variant_id === variantId;
    });
  }

  function lineFor(cart, variantId) {
    return linesFor(cart, variantId)[0] || null;
  }

  /*
    UNITS, NOT LINES.

    Both protection guards counted LINES: `protectionLines(cart).length === 1`
    was treated as correct. One line of quantity 4 satisfies that and charges
    £12.00 for a £3.00 service the shopper asked for once. Measured: a cart
    reached checkout as 1 line / 4 units with the guard reporting no
    correction needed. Duplicate LINES were being collapsed the whole time;
    the quantity underneath them was never looked at.
  */
  function unitsFor(cart, variantId) {
    var n = 0;
    linesFor(cart, variantId).forEach(function (i) { n += i.quantity || 0; });
    return n;
  }

  /*
    ============================================================
    ONE AUTHORITATIVE VALUE FOR EVERY REWARD DECISION

    Free gift threshold, free shipping threshold, the progress amount and
    the bar all read THIS and nothing else. They used to read
    cart.total_price independently, which includes the 3.00 Shipping
    Protection line, so the free-shipping message said

        139.99 - 132.99 = 7.00   "£7 AWAY FROM FREE UK SHIPPING"

    for an Academy cart whose actual merchandise is 129.99 and is therefore
    10.00 away. Worse, it CYCLED: protection is written and rewritten by
    more than one actor, so total_price alternated between 12999 and 13299
    and the message flipped 10 -> 7 -> 10 with the basket untouched.

    COMMERCIAL RULE: Shipping Protection must never move the customer's
    free-shipping or free-gift qualification.

    Summing final_line_price over merchandise gives the right answer with no
    Academy special case:
      valid pack   129.99 parent + five 0.00 components = 129.99, counted ONCE
      glove tier   the tier parent carries the price, its gloves are 0.00
    A broken pack counts what the customer is actually being charged, which
    is the honest figure to qualify on.

    NOTE, and it is a real divergence: Kaching's own gift automation is
    configured thresholdType "cart-total" with nothing excluded, and a cart
    of 77.98 merchandise plus 3.00 protection was measured unlocking the
    gift. While both carts run, Kaching may add the gift at a point this
    value says is not yet earned. Ours is the rule the owner specified.
    ============================================================
  */
  /*
    SHIPPING AND THE GIFT DO NOT SHARE A RULE, AND THAT IS NOT A PREFERENCE.

    Measured against Shopify's own rate endpoint for a real UK address,
    /cart/shipping_rates.json, twice on two different baskets:

      139.93 merchandise, no protection        Standard Delivery  4.95
      139.93 merchandise + 3.00 protection     Standard Delivery  0.00  FREE
      139.97 merchandise, no protection        Standard Delivery  4.95
      139.97 merchandise + 3.00 protection     Standard Delivery  0.00  FREE
      141.96 merchandise, no protection        Standard Delivery  0.00  FREE

    The only difference between the paid and free rows is the 3.00
    protection line. Shopify's price-based rate counts it. So a cart that
    excluded protection from the shipping bar told a customer at 139.97 with
    protection on that they were 0.02 away from a benefit checkout would
    already have given them. The bar must predict checkout, not our
    preferred definition of merchandise.

    The free GIFT is ours: we add and remove it, and the owner's rule is
    that protection must not buy it. So the two mechanics get two functions.

    Shipping reads the INTENDED protection state rather than whatever the
    server holds this millisecond. That is the state checkout will enforce,
    and it is why the message no longer flips on its own while protection
    converges: it moves when the customer moves the switch, and not
    otherwise.
  */
  /*
    What ONE unit of protection costs in this market, in presentment minor
    units. Priority is the cart's own line, because that is the number the
    shopper is actually being charged; then the Liquid-rendered market price,
    for the label shown before any line exists. If neither is available the
    answer is null - we do not know, and saying nothing is the only honest
    option left. Callers that need a number for GB arithmetic fall back to
    PROTECTION_PRICE themselves; nothing renders it.

    PROTECTION_PRICE used to be rendered directly. In Sweden that printed
    "3.00 SEK" against a 49.00 SEK charge - a 46 SEK hole between the toggle
    and the subtotal that the cart gave the shopper no way to explain.
  */
  function protectionUnitPrice(cart) {
    var l = lineFor(cart, PROTECTION_VARIANT);
    if (l) {
      if (typeof l.final_price === 'number') return l.final_price;
      if (l.quantity > 0) return Math.round((l.final_line_price || 0) / l.quantity);
    }
    if (protUnitPrice !== null) return protUnitPrice;
    return null;   /* unknown in this market - see protectionHtml */
  }

  /*
    The GB fallback. PROTECTION_PRICE is a GBP amount, so it is only ever
    reached on the UK path, where it is also the correct number; elsewhere
    the caller gets the market price or nothing.
  */
  function protectionAmount(cart) {
    var l = lineFor(cart, PROTECTION_VARIANT);
    if (l) return l.final_line_price || 0;
    var unit = protectionUnitPrice(cart);
    return unit !== null ? unit : PROTECTION_PRICE;
  }

  function giftQualifyingSubtotal(cart) {
    return qualifyingMerchandiseSubtotal(cart);
  }

  function shippingQualifyingSubtotal(cart) {
    return qualifyingMerchandiseSubtotal(cart) +
      (protectionWanted() ? protectionAmount(cart) : 0);
  }

  function qualifyingMerchandiseSubtotal(cart) {
    var total = 0;
    ((cart && cart.items) || []).forEach(function (i) {
      if (i.variant_id === PROTECTION_VARIANT) return;   /* a service charge */
      if (i.variant_id === GIFT_VARIANT) return;         /* a reward, not a purchase */
      total += i.final_line_price || 0;
    });
    return total;
  }

  /*
    Is the cart app this one replaces still on the page? Its embed renders a
    <kaching-cart> custom element, so the check is the app's own markup rather
    than a guess about globals. Evaluated at click time, long after the embed
    has loaded or not.
  */
  function kachingCartActive() {
    try { return !!document.querySelector('kaching-cart'); } catch (e) { return false; }
  }

  /*
    ============================================================
    PROTECTION INTENT IS THREE STATES, NOT TWO

    The store held 'on' or 'off' and read a MISSING key as ON. That made
    UNSET and ON indistinguishable, and reconcile() runs on drawer open and
    after every external cart write - so a shopper arriving with a cart built
    before this release, who had never been asked, gained GBP 3 simply by
    opening the site. Nobody chose that.

    UNSET now means UNSET: reconcile adds nothing. The default is applied at
    the only moment that represents a real decision point - the customer's
    own successful merchandise add - and is recorded then, so from that
    point on it is an explicit 'on' like any other.

      absent  -> UNSET  reconcile enforces 0 units, nothing is added
      'on'    -> ON     reconcile enforces exactly 1
      'off'   -> OFF    reconcile enforces exactly 0, forever

    An existing stored choice is never rewritten. Somebody who turned it off
    last week stays off; somebody already on stays on. There is deliberately
    no migration pass.
    ============================================================
  */
  function getProtectionIntent() {
    try {
      var v = localStorage.getItem(PROTECTION_PREF);
      if (v === 'on') return 'on';
      if (v === 'off') return 'off';
      return 'unset';
    } catch (e) { return 'unset'; }
  }

  function protectionWanted() {
    return getProtectionIntent() === 'on';
  }

  function setProtectionWanted(on) {
    try { localStorage.setItem(PROTECTION_PREF, on ? 'on' : 'off'); } catch (e) {}
  }

  function clearProtectionIntent() {
    try { localStorage.removeItem(PROTECTION_PREF); } catch (e) {}
    try { localStorage.removeItem(MERCH_SEEN); } catch (e) {}
  }

  /*
    ============================================================
    PROMOTION WATCHES THE CART, NOT THE REQUEST

    UNSET became ON by reading the body of a /cart/add.js call as it went
    past. That only works for adds WE can see. Zipify OCU intercepts the PDP
    Add to Cart and completes it through its own transport, so on the main
    purchase path the hook never fired, intent stayed UNSET, and protection
    rendered off - the default we had agreed was ON.

    This watches the outcome instead. Every cart read funnels through
    fetchCart, so comparing merchandise units against the last count we saw
    catches a cart that GREW regardless of who grew it: OCU, Kaching Bundles,
    Academy, BYOB, a theme form, anything.

    THE SEED IS WHAT MAKES THIS SAFE. The first time this browser is ever
    seen, the count is recorded and NOTHING is promoted. That is the whole
    difference between "the cart grew in front of us" and "a cart already
    existed when we arrived" - and it is why this is not the forbidden
    initialise-from-cart-presence, which would have put £3 on every legacy
    basket built before this feature existed.

    An explicit 'off' is still never touched. Only 'unset' is promoted.
    ============================================================
  */
  var MERCH_SEEN = 'hundred_cart_merch_seen';

  function merchandiseUnits(cart) {
    var n = 0;
    ((cart && cart.items) || []).forEach(function (i) {
      if (i.variant_id === PROTECTION_VARIANT) return;  /* a service charge */
      if (i.variant_id === GIFT_VARIANT) return;        /* a reward, not a purchase */
      n += i.quantity || 0;
    });
    return n;
  }

  function observeMerchandise(cart) {
    try {
      var now = merchandiseUnits(cart);
      var prevRaw = localStorage.getItem(MERCH_SEEN);
      localStorage.setItem(MERCH_SEEN, String(now));
      if (prevRaw === null) return cart;            /* first sight: seed only */
      var prev = Number(prevRaw);
      if (!isFinite(prev)) return cart;
      if (now > prev && getProtectionIntent() === 'unset') setProtectionWanted(true);
    } catch (e) {}
    return cart;
  }

  /*
    Does this /cart/add.js body represent a real customer buying something?

    Judged on WHAT IS BEING ADDED, not on price and not on who called fetch.
    Price is the wrong test because an Academy pack adds a paid parent
    alongside five children the BXGY will zero, and a glove tier adds free
    children under a priced parent - requiring every line to be paid would
    refuse both. The test is simply: is any line something other than the two
    mechanic variants?

    Protection adding itself, and the free gift being added by reconcile,
    therefore cannot promote intent - which is what stops the obvious
    recursion of "promote -> add protection -> promote".
  */
  function addIsCustomerMerchandise(init) {
    try {
      var body = init && init.body;
      if (!body) return false;
      var ids = [];
      if (typeof body === 'string') {
        var j = JSON.parse(body);
        if (j && j.items && j.items.length) {
          ids = j.items.map(function (i) { return Number(i.id); });
        } else if (j && j.id) {
          ids = [Number(j.id)];
        }
      } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var one = body.get('id');
        if (one) ids = [Number(one)];
      } else {
        return false;   /* unreadable body: do not guess, do not promote */
      }
      return ids.some(function (id) {
        return id && id !== PROTECTION_VARIANT && id !== GIFT_VARIANT;
      });
    } catch (e) { return false; }
  }

  /* Adds this file makes itself must not come back round through the open
     hook: the drawer is already open and already re-renders when the promise
     resolves, so letting the hook fire produced a second, later render that
     landed on top of the shopper's half-finished size choice. */
  var internalAdd = 0;

  /*
    ============================================================
    OUR WRITES, AND EVERYONE ELSE'S

    The fetch hook only ever watched /cart/add.js, so an external ADD was
    noticed and an external REMOVE was not. Measured: an external removal
    left the drawer showing £82.98 against a server holding £60.99, and an
    externally corrupted Academy left a healthy £132.99 pack on screen while
    the cart actually held £283.95. The customer was looking at money that
    no longer existed.

    `internalWrite` marks the window around this file's own change/update
    calls, so the observer can tell a write it made from one it did not.
    ============================================================
  */
  var internalWrite = 0;
  var externalSyncTimer = null;

  function markOurWrite(promise) {
    internalWrite++;
    var done = function () {
      /* A beat after the response, so the hook - which runs on the same
         response - still sees the marker. */
      setTimeout(function () { internalWrite = Math.max(0, internalWrite - 1); }, 60);
    };
    return promise.then(
      function (v) { done(); return v; },
      function (e) { done(); throw e; }
    );
  }

  /*
    ============================================================
    USE THE CART THE MUTATION ALREADY RETURNED

    Measured before this: one Remove cost 21 requests, 11 of them /cart.js,
    and took 5.4s to settle. One protection toggle cost 34 requests and
    10.5s. The cause was that every corrective step threw away the cart in
    its own mutation response and read the whole thing again:

        removeLine(key).then(fetchCart).then(reconcile)

    /cart/change.js and /cart/update.js both RETURN the full updated cart, so
    that read was pure waste, and because reconcile recurses it was paid
    again on every pass.

    /cart/add.js is the exception: it returns the added lines, not the cart.
    Adds therefore go through update.js, which sets an absolute quantity and
    hands back the cart. That is exactly the semantics the mechanic lines
    want anyway: protection and the gift are always exactly one.
    ============================================================
  */
  function changeLine(key, quantity) {
    return queueWrite(function () {
    return markOurWrite(fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })).then(function (r) { return r.json(); }).then(stamp);
    });
  }

  function setLine(variantId, quantity) {
    var updates = {};
    updates[variantId] = quantity;
    return queueWrite(function () {
      return markOurWrite(fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      })).then(function (r) { return r.json(); }).then(stamp);
    });
  }

  function addLine(variantId) {
    internalAdd++;
    setTimeout(function () { internalAdd = Math.max(0, internalAdd - 1); }, 3000);
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: { _hundred_cart: '1' }
        }]
      })
    });
  }

  function removeLine(key) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
  }

  /*
    ATOMIC VARIANT SWAP.

    /cart/change.js cannot change a variant, only a quantity, so a swap has
    to be a removal and an add. Doing it as two requests leaves a window in
    which the Academy pack is five lines instead of six and the Function can
    re-evaluate against a pack that is missing a component.

    /cart/update.js takes absolute quantities keyed by variant id and applies
    them in ONE request, so the cart is never observed mid-swap. Verified on
    a real Academy cart: Core Skin Top YS -> YM, and Core Skin Top -> Protekt
    Padded Top, both left the six line payload, the 129.99 parent and all
    five 0.00 components exactly as they were.
  */
  /*
    SWAP BY LINE KEY, NOT VARIANT ID.

    This keyed the removal on the variant id, which is the same identity bug
    that broke Remove pack. An Academy component can share a variant with a
    standalone line elsewhere in the cart: the child carries no properties
    and a PDP add carries Kaching's, so they sit as two separate lines on the
    same variant. Zeroing that variant is ambiguous at best and takes the
    shopper's own item at worst.

    The removal is addressed by the child's LINE KEY. The addition stays a
    variant id because the new size is not in the cart yet, and both travel
    in one /cart/update.js so the pack is never seen with five components.
  */

  /*
    Bring the cart in line with the two rules, one change at a time, then
    re-read. Returns the cart to render.

    Order matters: protection first, because it counts toward the gift
    threshold, then the gift, which is £0 and therefore cannot change the
    total it was judged against.
  */
  function reconcile(cart) {
    /* The single chokepoint every housekeeping write passes through. */
    if (backgroundWritesBlocked()) return Promise.resolve(cart);

    /*
      COLLAPSE DUPLICATES FIRST.

      Shopify keys a cart line on variant AND properties, so the same variant
      added with different properties becomes two lines. Kaching writes its
      protection line with `_kaching_cart` and this file writes its own with
      `_hundred_cart`, and while the flag is on both are running, so the cart
      can legitimately end up holding two £3.00 Shipping Protection lines and
      charging for both. Exactly one of each mechanic line, always.
    */
    var dupes = linesFor(cart, PROTECTION_VARIANT).slice(1)
      .concat(linesFor(cart, GIFT_VARIANT).slice(1));
    if (dupes.length) {
      return changeLine(dupes[0].key, 0).then(reconcile);
    }

    /*
      Bundle tier first. It changes total_price, and the gift threshold is
      judged on total_price, so normalising afterwards would test the gift
      against a price the cart is about to stop charging.
    */
    var want = desiredBundle(cart);
    var tierWrong = bundles().some(function (b) {
      var line = lineFor(cart, b.id);
      var have = line ? line.quantity : 0;
      return have !== ((want && b.id === want.id) ? 1 : 0);
    });
    if (tierWrong) {
      return normaliseBundle(cart).then(reconcile);
    }

    var prot = lineFor(cart, PROTECTION_VARIANT);
    var wantProt = protectionWanted();

    /* An empty cart stays empty. Protection is an add-on to an order; adding
       it to nothing produced a "£3.00 empty cart" with a reward bar and an
       upsell, which is nonsense. Merchandise means anything that is not one
       of the two mechanic lines. */
    var merchandise = (cart.items || []).filter(function (i) {
      return i.variant_id !== PROTECTION_VARIANT && i.variant_id !== GIFT_VARIANT;
    }).length;

    if (!merchandise) {
      if (prot) return changeLine(prot.key, 0).then(reconcile);
      var strayGift = lineFor(cart, GIFT_VARIANT);
      if (strayGift) return changeLine(strayGift.key, 0);
      return Promise.resolve(cart);
    }

    /*
      Protection is judged on UNITS. `prot` being present said nothing about
      how many the shopper was being charged for.
    */
    var protUnits = unitsFor(cart, PROTECTION_VARIANT);
    if (protUnits !== (wantProt ? 1 : 0)) {
      return setLine(PROTECTION_VARIANT, wantProt ? 1 : 0).then(reconcile);
    }

    /* Same authoritative value the bar uses: the two can never disagree. */
    var giftUnits = unitsFor(cart, GIFT_VARIANT);
    var wantGift;
    if (isUK()) {
      wantGift = giftQualifyingSubtotal(cart) >= GIFT_THRESHOLD ? 1 : 0;
    } else {
      /*
        Outside GB we never ADD: the threshold that would have granted it is
        80 units of the local currency, which is not a rule we stand behind.

        But we do not take one back either. A gift already sitting in a cart
        was shown to that shopper as theirs, and snatching it away mid-purchase
        to correct our own arithmetic costs more trust than the item is worth -
        it is a £0.00 digital download. So: keep exactly one where one exists,
        trim duplicates, and never create the first.
      */
      wantGift = giftUnits > 0 ? 1 : 0;
    }

    /*
      ONE reward or none, counted in units across every line holding the gift
      variant. This was three separate checks keyed on the FIRST line only -
      add, remove, and a quantity trim - so two gift lines, or one line of
      quantity 2, could survive a pass. A variant-quantity update settles all
      of them at once.
    */
    if (giftUnits !== wantGift) {
      var wasEarned = wantGift === 1 && giftUnits === 0;
      return setLine(GIFT_VARIANT, wantGift).then(function (c) {
        if (wasEarned) {
          track('hundred_cart_reward_unlocked', {
            cart_value: c.total_price, currency: c.currency
          });
        }
        return c;
      });
    }
    return Promise.resolve(cart);
  }

  /*
    ============================================================
    THE SWITCH MOVES ON THE TAP, NOT ON THE ROUND TRIP

    Reported as difficult and unreliable on mobile, and it was, for two
    reasons that had nothing to do with the size of the target:

    1  The switch only moved after fetch -> reconcile -> render. A tap
       produced no visible change for the length of a server round trip, so
       it read as a missed tap.
    2  `if (busy || protectionPending) return` silently DROPPED any second
       tap, and protectionPending stayed true through a 1200ms settle. Tap,
       see nothing, tap again, get ignored.

    Intent is already this drawer's source of truth for the switch, so the
    tap can be honoured immediately: flip the intent, paint the switch, and
    let the write follow. A tap that arrives mid-write is remembered rather
    than discarded, and applied when the current one lands.

    The 1200ms settle existed only to outwait Kaching re-adding the line. It
    now runs ONLY if Kaching Cart is actually present, so in the production
    architecture the toggle settles as fast as the cart replies.
    ============================================================
  */
  var protectionRetap = false;

  function paintProtectionSwitch() {
    try {
      var btn = root.querySelector('[data-hcd-protection]');
      if (btn) btn.setAttribute('aria-checked', protectionWanted() ? 'true' : 'false');
    } catch (e) {}
  }

  function toggleProtection() {
    if (backgroundWritesBlocked()) return;   /* checkout has the final say */
    setProtectionWanted(!protectionWanted());
    paintProtectionSwitch();                 /* immediate, on every tap */
    if (busy || protectionPending) {
      /* A write is in flight. Remember the newest intent instead of
         throwing the tap away; it is applied when that write lands. */
      protectionRetap = true;
      return;
    }
    runProtectionWrite();
  }

  function runProtectionWrite() {
    protectionPending = true;
    track('hundred_cart_protection_toggle', { enabled: protectionWanted() });
    busy = true;
    fetchCart().then(reconcile).then(render)
      .then(function () {
        /*
          Kaching's embed is still running while the flag is on and re-adds
          protection after we remove it. Settle, then render the SERVER's
          answer rather than ours, so the drawer can never show a subtotal
          the cart disagrees with. The toggle becomes durable at cutover,
          when that embed is disabled.
        */
        if (!kachingCartActive()) return null;   /* nothing left to outwait */
        return new Promise(function (r) { setTimeout(r, 1200); })
          .then(fetchCart).then(render);
      })
      .catch(function (err) {
        /* The correction failed, so intent cannot stand: restore what the
           server actually holds and say so, rather than leaving the switch
           showing a choice that was never applied. */
        try { console.warn('[hundred-cart] protection change failed', err); } catch (x) {}
        return fetchCart().then(function (c) {
          setProtectionWanted(!!lineFor(c, PROTECTION_VARIANT));
          protectionPending = false;
          render(c);
          var el = root.querySelector('[data-hcd-checkout-error]');
          if (el) { el.textContent = 'Could not update Shipping Protection. Please try again.'; el.hidden = false; }
        }).catch(function () {});
      })
      .then(function () {
        protectionPending = false;
        busy = false;
        /* A tap that arrived mid-write is applied now, so the switch always
           ends where the customer last left it. */
        if (protectionRetap) { protectionRetap = false; return runProtectionWrite(); }
        /*
          Goes through the same comparison as every other paint. This was a
          direct innerHTML write, so it rebuilt the switch a second time
          after the toggle had already settled it: same state, new DOM node,
          visible repaint. Measured as a third ROW REBUILT at ~2.2s with no
          change behind it.
        */
        if (lastCart) {
          var h = protectionHtml(lastCart);
          if (h !== lastProtHtml) { protSlotEl.innerHTML = h; lastProtHtml = h; }
        }
      });
  }

  function open() {
    /*
      Lock the background WITHOUT losing the reader's place. Measured:
      overflow on the root alone reset the page from 883px to 0 on mobile,
      so closing the cart dropped the shopper at the top of a page they had
      scrolled a long way down. Pin the body at a negative offset instead
      and put it back on close.
    */
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    /*
      ============================================================
      DO NOT SHOW £0.00 TO SOMEONE WHO JUST ADDED SOMETHING

      MEASURED on a glove add: the drawer became visible at 840ms reading
      "Your cart is empty" and "£0.00", and the real £57.99 landed at
      1046ms. Two hundred milliseconds of being told your cart is empty,
      on every single add, for every customer.

      The number was never wrong in the data - it is the initial empty
      paint from page load, still sitting in the DOM because open() reveals
      the panel immediately and only then fetches.

      Revealing later would trade a wrong number for a slow one, so the
      panel still opens at once and the two things that would be actively
      untrue are held back until the first paint lands. Nothing moves when
      they appear: they are hidden with visibility, not display, so the
      footer does not jump.
      ============================================================
    */
    if (!lastCart || !lastCart.item_count) {
      openPaintPending = true;
      root.setAttribute('data-hcd-loading', '');
    }
    root.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + savedScrollY + 'px';
    document.body.style.width = '100%';

    paintThenSettle(fetchCart()).then(function (c) {
      openPaintPending = false;
      root.removeAttribute('data-hcd-loading');
      if (!c) return;
      /* Tracked from the cart we already have. This used to be a SECOND
         /cart.js call fired alongside the first, doubling the requests on
         every open for an analytics payload. */
      track('hundred_cart_open', {
        cart_value: c.total_price,
        currency: c.currency,
        academy_present: !!group(c).packs.length,
        free_gift_unlocked: !!lineFor(c, GIFT_VARIANT),
        free_shipping_unlocked: c.total_price >= SHIPPING_THRESHOLD,
        shipping_protection_enabled: !!lineFor(c, PROTECTION_VARIANT)
      });
    }).catch(function () {});

    var c = root.querySelector('[data-hcd-close]');
    if (c) c.focus();
  }

  function close() {
    openPaintPending = false;
    if (root) root.removeAttribute('data-hcd-loading');
    closeSheet();
    root.hidden = true;
    document.documentElement.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, savedScrollY);
  }

  function init() {
    root = document.querySelector('[data-hcd]');
    if (!root) return;
    bodyEl = root.querySelector('[data-hcd-body]');
    listEl = root.querySelector('[data-hcd-list]');
    sheetEl = root.querySelector('[data-hcd-sheet]');
    sheetTitleEl = root.querySelector('[data-hcd-sheet-title]');
    sheetBodyEl = root.querySelector('[data-hcd-sheet-body]');
    scrimEl = root.querySelector('.hcd__scrim');
    upsellEl = root.querySelector('[data-hcd-upsell]');
    protSlotEl = root.querySelector('[data-hcd-protection-slot]');
    protImg = root.getAttribute('data-hcd-prot-img') || null;
    countryCode = (root.getAttribute('data-hcd-country') || '').toUpperCase() || null;
    /* A zero is not a price, it is a missing lookup - the variant unpublished
       from this market, or all_products coming back blank. Treated as UNKNOWN
       so the label can stay silent instead of announcing protection as free. */
    var protPriceAttr = root.getAttribute('data-hcd-prot-price');
    protUnitPrice = (protPriceAttr !== null && protPriceAttr !== '' && Number(protPriceAttr) > 0)
      ? Number(protPriceAttr)
      : null;
    subtotalEl = root.querySelector('[data-hcd-subtotal]');
    ladderEl = root.querySelector('[data-hcd-ladder]');
    savingEl = root.querySelector('[data-hcd-saving]');

    bodyEl.addEventListener('scroll', scrollCue, { passive: true });

    /* Passive, and it only records a number: no layout writes, no render. */
    listEl.addEventListener('scroll', function (e) {
      var r = e.target;
      if (r && r.hasAttribute && r.hasAttribute('data-hcd-rail')) {
        railScrollLeft = r.scrollLeft;
      }
    }, true);
    window.addEventListener('resize', scrollCue);

    protSlotEl.addEventListener('click', function (e) {
      if (e.target.closest('[data-hcd-protection]')) toggleProtection();
    });

    /* Bound on the LIST, because the recommendation now renders inside it.
       Left on the old empty slot this button would have been dead on
       arrival, which is precisely how the rotation CTA broke. */
    listEl.addEventListener('click', function (e) {
      var add = e.target.closest('[data-hcd-upsell-add]');
      if (!add || busy) return;
      var card = add.closest('[data-hcd-upsell-card]');
      if (!card) return;
      var handle = card.getAttribute('data-hcd-upsell-card');
      var u = byHandle(handle);
      if (!u) return;

      /*
        NOTHING IS ADDED AT A SIZE THE SHOPPER DID NOT PICK.

        A sized card with no selection sends them to the selector instead of
        quietly adding whatever came back first. The button stays live so the
        tap does something: a CTA that looks enabled and silently fails is
        how the glove card broke before.
      */
      var v = upsellVariant(u);
      if (!v) {
        var warn = card.querySelector('[data-hcd-upsell-warn]');
        if (warn) warn.hidden = false;
        var sel = card.querySelector('[data-hcd-upsell-size]');
        if (sel && sel.focus) sel.focus();
        return;
      }

      busy = true;
      addLine(v.id)
        .then(fetchCart).then(reconcile)
        .then(function (cart) {
          /*
            ADVANCE THE FUNNEL IMMEDIATELY.

            render() re-runs pickUpsell against the cart that now contains
            what was just accepted, so the next offer is on screen in the
            same frame the line appears. There is no blank recommendation
            state between stages.
          */
          delete selectedSizes[handle];
          lastUpsellSeen = null;
          render(cart);
          track('hundred_cart_upsell_add', {
            upsell_product: handle, variant_id: v.id, category: u.cat,
            cart_value: cart.total_price, currency: cart.currency
          });
        })
        .catch(function () {})
        .then(function () { busy = false; });
    });

    /* The upsell size selector. Local state only, exactly like the glove
       card: no fetch, no reconcile, no render, so the choice cannot be
       destroyed by the repaint its own change would have triggered. */
    listEl.addEventListener('change', function (e) {
      var sel = e.target.closest('[data-hcd-upsell-size]');
      if (!sel) return;
      var card = sel.closest('[data-hcd-upsell-card]');
      if (!card) return;
      var handle = card.getAttribute('data-hcd-upsell-card');
      if (sel.value) selectedSizes[handle] = parseInt(sel.value, 10);
      else delete selectedSizes[handle];
      var warn = card.querySelector('[data-hcd-upsell-warn]');
      if (warn) warn.hidden = true;
    });

    /* Open the picker. Bound on the list because the rotation offer is a row
       in the list now, not a footer block. */
    /* A size has to be chosen before ADD is live: nothing is ever added at a
       default size the shopper did not pick. */
    listEl.addEventListener('change', function (e) {
      var sel = e.target.closest('[data-hcd-card-size]');
      if (!sel) return;
      var card = sel.closest('[data-hcd-card]');
      if (!card) return;
      /* Local state only. No fetch, no reconcile, no render. */
      var handle = card.getAttribute('data-hcd-card');
      if (sel.value) {
        selectedSizes[handle] = parseInt(sel.value, 10);
        card.removeAttribute('data-hcd-needs-size');
      } else {
        delete selectedSizes[handle];
      }
      var r = listEl.querySelector('[data-hcd-rail]');
      if (r) railScrollLeft = r.scrollLeft;
    });

    listEl.addEventListener('click', function (e) {
      var cardAdd = e.target.closest('[data-hcd-card-add]');
      if (cardAdd && !busy) {
        var card = cardAdd.closest('[data-hcd-card]');
        var sel = card && card.querySelector('[data-hcd-card-size]');
        var vid = sel && parseInt(sel.value, 10);

        /* No size yet: send them to the control that is actually blocking
           them rather than doing nothing. */
        if (!vid) {
          if (sel) {
            sel.focus();
            try { if (sel.showPicker) sel.showPicker(); } catch (err) {}
            card.setAttribute('data-hcd-needs-size', '');
          }
          return;
        }

        /*
          REGRESSION FIXED. This read `pending.kind !== 'next'`, left over
          from when ladderOffer returned claim/next/top kinds. That field was
          removed when the tier started applying itself, so `kind` was
          undefined, the comparison was always true, and the button returned
          without adding anything EVEN WITH A SIZE CHOSEN. The CTA was dead
          in every state.
        */
        var pending = ladderOffer(lastCart);
        if (!pending || !pending.tier) return;
        card.removeAttribute('data-hcd-needs-size');
        busy = true;
        applyTier(pending.tier.qty, vid)
          .then(fetchCart).then(reconcile)
          .then(function (cart) {
            /* In place. The drawer stays open, the tier recalculates, the
               rail rebuilds and the rewards move. Nothing replays the
               opening animation. */
            render(cart);
            var after = ladderOffer(cart);
            track('glove_offer_pair_added', {
              glove_handle: card.getAttribute('data-hcd-card'), variant_id: vid,
              current_pair_count: countedGloves(cart).qty,
              next_pair_count: after && after.tier ? after.tier.qty : null,
              current_tier: pending.tier.qty,
              next_tier: after && after.tier ? after.tier.qty : null,
              incremental_price: after && after.incremental ? after.incremental : null,
              cart_value: cart.total_price, currency: cart.currency
            });
            document.dispatchEvent(new CustomEvent('hundred:cart:updated', { detail: { cart: cart } }));
          })
          .catch(function () {})
          .then(function () { busy = false; busy = false; });
        return;
      }

      /*
        REMOVE PACK TAKES THE WHOLE PACK, IN ONE REQUEST.

        This used to fall through to the generic remove, which resolves
        data-line on the .hcd__pack element: the ACADEMY PARENT key. Only the
        parent came out. Measured on a real pack: 8 lines at £132.99 became 7
        lines at £173.95, because losing the parent ends the BXGY and the
        five components revert to full price. The shopper pressed "Remove
        pack" and their cart got £40.96 more expensive, holding five items
        they never chose individually.

        All six lines are zeroed in a single /cart/update.js, keyed on LINE
        KEY rather than variant id: a component can share a variant with a
        loose line elsewhere in the cart, and those merge, so removing by
        variant would take the shopper's separate item with it.
      */
      /*
        ============================================================
        RESET CART - THE ESCAPE HATCH THAT NEEDS NO LINE OWNERSHIP

        Every other recovery in this drawer resolves which lines belong to
        what: remove-pack needs pack keys, remove-line needs a key, the size
        sheet needs an intact pack. All of them are unavailable in exactly
        the state that strands a shopper - the one where ownership cannot be
        proven. /cart/clear.js needs none of it.

        Two taps, never one: the confirm step exists because this throws
        away everything, including items the shopper chose deliberately.

        Marked as our own write so the reconciler does not treat the
        emptied cart as somebody else's change and helpfully re-add
        Shipping Protection or the free gift into a cart the shopper just
        asked to be empty.
        ============================================================
      */
      var rst = e.target.closest('[data-hcd-reset]');
      if (rst && !busy) {
        if (rst.getAttribute('data-hcd-reset') !== 'confirm') {
          var host = rst.closest('.hcd__pack-meta') || rst.parentNode;
          rst.hidden = true;
          var box = document.createElement('div');
          box.className = 'hcd__reset-confirm';
          box.innerHTML =
            '<p class="hcd__reset-q">Reset your cart?</p>' +
            '<p class="hcd__reset-sub">This removes everything in your cart so you can start again.</p>' +
            '<div class="hcd__reset-actions">' +
              '<button type="button" class="hcd__reset-cancel" data-hcd-reset-cancel>CANCEL</button>' +
              '<button type="button" class="hcd__pack-reset" data-hcd-reset="confirm">RESET CART</button>' +
            '</div>';
          host.appendChild(box);
          return;
        }
        busy = true;
        rst.disabled = true;
        rst.textContent = 'RESETTING\u2026';
        markOurWrite(fetch('/cart/clear.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
        }))
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            /* Authoritative read: clear.js returns the cart, but this state
               exists because something upstream was not trustworthy. */
            return markOurWrite(fetch('/cart.js', { headers: { Accept: 'application/json' } }))
              .then(function (r2) { return r2.json(); })
              .catch(function () { return cart; });
          })
          .then(function (cart) {
            /*
              A reset is a genuine fresh start, so the protection preference
              goes back to UNSET and the next real merchandise add will
              default it on again. Normal emptying does NOT do this: someone
              who deliberately turned protection off and then removed their
              last item should not be quietly opted back in when they add
              something new. Only this emergency recovery forgets the choice.
            */
            clearProtectionIntent();
            /* Deliberately NOT reconcile(): reconcile is what re-adds
               protection and the gift. A cart the shopper just emptied must
               stay empty. */
            lastCart = cart;
            render(cart);
            try {
              track('hundred_cart_reset', {
                cart_value: 0, currency: (cart && cart.currency) || 'GBP'
              });
            } catch (x) {}
          })
          .catch(function (err) {
            try { console.warn('[hundred-cart] reset failed', err); } catch (x) {}
            rst.disabled = false;
            rst.textContent = 'RESET CART';
          })
          .then(function () { busy = false; });
        return;
      }

      var cancelReset = e.target.closest('[data-hcd-reset-cancel]');
      if (cancelReset) {
        var wrap = cancelReset.closest('.hcd__reset-confirm');
        var meta = wrap && wrap.parentNode;
        if (wrap) wrap.parentNode.removeChild(wrap);
        if (meta) {
          var hidden = meta.querySelector('[data-hcd-reset]');
          if (hidden) hidden.hidden = false;
        }
        return;
      }

      var rp = e.target.closest('[data-hcd-remove-pack]');
      if (rp && !busy) {
        var keys;
        try { keys = JSON.parse(rp.getAttribute('data-hcd-remove-pack')); } catch (err) { keys = []; }
        if (!keys.length) return;
        busy = true;
        var updates = {};
        keys.forEach(function (k) { updates[k] = 0; });
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        })
          .then(function (r) { return r.json(); })
          .then(stamp)
          .then(function (cart) { render(cart); return reconcile(cart); })
          .then(function (cart) {
            render(cart);
            track('hundred_cart_academy_removed', {
              cart_value: cart.total_price, currency: cart.currency
            });
          })
          .catch(function () { return fetchCart().then(render); })
          .then(function () { busy = false; busy = false; });
        return;
      }

      var ch = e.target.closest('[data-hcd-change]');
      if (ch) {
        openAcademySize(
          ch.getAttribute('data-hcd-change'),
          parseInt(ch.getAttribute('data-hcd-change-variant'), 10),
          ch.getAttribute('data-hcd-change-key'),
          ch.getAttribute('data-hcd-change-name')
        );
      }
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-hcd-sheet-close]'), function (el) {
      el.addEventListener('click', closeSheet);
    });

    sheetBodyEl.addEventListener('click', function (e) {
      var size = e.target.closest('[data-hcd-size]');
      if (size) {
        Array.prototype.forEach.call(sheetBodyEl.querySelectorAll('[data-hcd-size]'), function (b) {
          b.setAttribute('aria-pressed', b === size ? 'true' : 'false');
        });
        if (sheetState) sheetState.selected = parseInt(size.getAttribute('data-hcd-size'), 10);
        var cta = sheetBodyEl.querySelector('[data-hcd-size-save]');
        if (cta) cta.disabled = false;
        return;
      }

      /*
        ============================================================
        IN-CART ACADEMY SIZE MUTATION IS DISABLED.

        Reported and reproduced by the owner: changing a size through this
        control turned a valid £129.99 Academy Pack into roughly £256.96 of
        retail lines. The pack grouping vanished and former components
        appeared as ordinary priced items.

        The mechanism is the BXGY. "ACADEMY PACK = More Savings" is a
        buy-1-get-5 automatic discount over a fixed eligible product set. A
        replacement child added as a plain variant is not guaranteed to be in
        that set, and the discount needs its full quota of eligible items: if
        it cannot allocate five, it releases, and every component reverts to
        full price while the parent keeps charging £129.99.

        Our own grouping then collapses too, because a pack is only drawn
        when there is a parent AND at least one £0.00 line carrying the
        Academy allocation. With the discount released there are none, so the
        six lines render as ordinary merchandise. That is the screenshot.

        The Academy backend is frozen, so the replacement cannot be made
        semantically identical to a builder-created child from here. Until
        that is proven, no mutation is issued at all. A control that cannot
        corrupt the cart is worth more than a convenience that can.
        ============================================================
      */
      var save = e.target.closest('[data-hcd-size-save]');
      if (save) {
        if (!sheetState || !sheetState.selected || save.disabled) return;
        var pack = null;
        var g = lastCart ? group(lastCart) : { packs: [] };
        g.packs.forEach(function (pk) { if (!pack) pack = pk; });
        if (!pack || pack.intact === false) {
          var e0 = sheetBodyEl.querySelector('[data-hcd-size-error]');
          if (e0) { e0.textContent = 'This pack cannot be changed right now.'; e0.hidden = false; }
          return;
        }
        save.disabled = true;
        save.textContent = 'SAVING\u2026';
        replaceAcademyPack(pack, sheetState.fromKey, sheetState.selected)
          .then(function (cart) {
            closeSheet();
            return paintThenSettle(Promise.resolve(cart));
          })
          .catch(function (err) {
            try { console.warn('[hundred-cart] academy size change failed', err); } catch (x) {}
            save.disabled = false;
            save.textContent = 'SAVE CHANGE';
            var el = sheetBodyEl.querySelector('[data-hcd-size-error]');
            if (el) {
              el.textContent = 'Could not change that size. Your pack has not been altered.';
              el.hidden = false;
            }
          });
        return;
      }

    });

    /*
      ============================================================
      THE CHECKOUT INVARIANT

      Kaching Cart and this drawer both write the Shipping Protection line
      while both are live, so an opted-out cart can transiently hold one.
      The drawer converges, but a shopper who presses Checkout inside that
      window would be charged £3.00 they declined.

      So the LAST thing that happens before navigation is a check that the
      server cart agrees with the customer's stored intent:

        intent OFF  ->  exactly 0 protection lines
        intent ON   ->  exactly 1 protection line

      Only the checkout action pays this cost. Normal rendering is untouched.
      ============================================================
    */
    var checkoutForm = root.querySelector('[data-hcd-checkout-form]');
    var checkoutBtn = root.querySelector('[data-hcd-checkout]');
    var checkingOut = false;

    function protectionLines(cart) {
      return linesFor(cart, PROTECTION_VARIANT);
    }

    /*
      TARGETED BY VARIANT, AND RETRIED.

      This addressed the protection lines by LINE KEY, read a moment earlier.
      Kaching writes the same line, so by the time the correction was sent the
      key could already be gone and Shopify answered 422 Unprocessable Entity:
      the correction failed, the guard refused to proceed, and checkout became
      unreachable in coexistence. Measured exactly that.

      Protection is the one case where variant targeting is correct rather
      than ambiguous: the goal is "this variant, exactly N in the cart",
      whoever created the lines and however many there are. A variant id
      cannot go stale the way a line key can.

      Two attempts, because the actor we are racing is still writing. If it
      genuinely will not converge we refuse rather than send a cart that
      contradicts the customer's choice.
    */
    function enforceProtectionIntent(attempt) {
      attempt = attempt || 1;
      var want = protectionWanted();

      return fetchCart().then(function (cart) {
        var lines = protectionLines(cart).length;
        var units = unitsFor(cart, PROTECTION_VARIANT);
        var wantUnits = want ? 1 : 0;
        if (lines <= 1 && units === wantUnits) return cart;

        /*
          A variant-quantity update sets the TOTAL for that variant, which is
          exactly what is needed here: it collapses several lines and any
          excess quantity to the one number the shopper intended.
        */
        var updates = {};
        updates[PROTECTION_VARIANT] = wantUnits;

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        })
          .then(function (r) { return r.json().catch(function () { return null; }); })
          .then(fetchCart)
          .then(function (fresh) {
            var gotLines = protectionLines(fresh).length;
            var gotUnits = unitsFor(fresh, PROTECTION_VARIANT);
            if (gotLines <= 1 && gotUnits === wantUnits) { render(fresh); return fresh; }
            if (attempt < 2) return enforceProtectionIntent(attempt + 1);
            throw new Error('protection invariant not met: wanted ' + wantUnits +
              ' unit(s), got ' + gotUnits + ' across ' + gotLines + ' line(s)');
          });
      });
    }

    /*
      The control is released through ONE path, and a watchdog guarantees it:
      a checkout that neither navigates nor errors within 8s must not leave
      the customer looking at a dead button.
    */
    var checkoutWatchdog = null;

    function release(msg) {
      checkingOut = false;
      checkoutFinalizing = false;   /* the drawer owns the cart again */
      if (checkoutBtn) {
        checkoutBtn.disabled = false;
        checkoutBtn.removeAttribute('disabled');
        checkoutBtn.removeAttribute('data-hcd-pending');
        checkoutBtn.removeAttribute('aria-busy');
      }
      clearTimeout(checkoutWatchdog);
      checkoutError(msg || '');
    }

    function checkoutError(msg) {
      var el = root.querySelector('[data-hcd-checkout-error]');
      if (el) { el.textContent = msg || ''; el.hidden = !msg; }
    }

    /*
      BOUND ON THE BUTTON'S CLICK, NOT THE FORM'S SUBMIT.

      With Kaching Cart active it also binds the checkout form, and our
      submit handler could start (button disabled, aria-busy set) and then
      never complete: measured with protection ALREADY correct, so no
      correction was even needed, the button was left disabled with no error
      and no navigation. Checkout was unreachable in coexistence.

      Listening on the button's own click in the capture phase, and stopping
      the event there, means no other listener and no native form submit can
      take the flow away from us. The form keeps action="/cart" purely as a
      no-JS fallback.
    */
    if (checkoutBtn) {
      /*
        BOUND ON WINDOW CAPTURE, for the same reason the cart triggers are:
        Kaching's embed loads in the head and registers its own capture
        listener on DOCUMENT before this file has parsed, so a listener on
        the button or the form fires second and can be stopped before it
        runs. Capture order is window -> document -> target, so window is the
        only place the interception is reliable.

        Measured: with a button-level listener, coexistence checkout never
        navigated at all while target mode went through in 1.2s.
      */
      /*
        ============================================================
        SILENCE OTHER LISTENERS ONLY WHILE THERE IS SOMETHING TO SILENCE

        This used to call stopImmediatePropagation unconditionally, which was
        right when Kaching Cart owned the same button: its document-capture
        listener would otherwise take the flow away from us, and coexistence
        checkout never navigated at all without it.

        But it also silenced every unrelated listener on the click - the
        Shopify Web Pixels Manager, the TikTok pixel and gtag among them - and
        in the production architecture Kaching Cart is DISABLED, so there is
        nothing left to beat. Keeping the suppression there would be paying a
        permanent analytics cost to win a fight that is over.

        preventDefault stays unconditional: the form posts to /cart as a
        no-JS fallback and we navigate to the hosted checkout ourselves.
        Propagation is stopped ONLY when Kaching Cart is actually on the page,
        detected by its own custom element. Measured in target mode:
        document.querySelectorAll('kaching-cart').length === 0, with only
        Kaching BUNDLES still loaded, which is the intended production shape.
        ============================================================
      */
      window.addEventListener('click', function (e) {
        if (!e.target.closest || !e.target.closest('[data-hcd-checkout]')) return;
        e.preventDefault();
        if (kachingCartActive()) {
          e.stopPropagation();
          if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        }
        if (checkingOut) return;                 /* one flow, one navigation */
        checkingOut = true;
        /* From here nothing of ours may write to the cart until we navigate
           or release. See the barrier note above queueWrite(). */
        checkoutFinalizing = true;
        checkoutError('');
        checkoutBtn.disabled = true;
        checkoutBtn.setAttribute('data-hcd-pending', '');
        checkoutBtn.setAttribute('aria-busy', 'true');
        clearTimeout(checkoutWatchdog);
        checkoutWatchdog = setTimeout(function () {
          release('Something went wrong. Please try checkout again.');
        }, 8000);

        /*
          THE BARRIER, IN ORDER.

          New background writes are already refused by checkoutFinalizing.
          What remains is to let anything ALREADY in flight finish - both the
          drawer's own lifecycle flags and the serialised write chain - so
          the cart we then read is not about to be overwritten by a write
          issued before the click.
        */
        var waited = 0;
        (function settle() {
          if ((busy || protectionPending) && waited < 4000) {
            waited += 100;
            return setTimeout(settle, 100);
          }
          queueWrite(function () { return Promise.resolve(); })
            .then(function () { return enforceProtectionIntent(); })
            .then(function (cart) {
              /* Never knowingly send a corrupted pack to checkout. */
              var acad = academyIntegrity(cart);
              if (acad.present && !acad.valid) {
                throw new Error('academy integrity: ' + acad.parents +
                  ' parent(s), ' + acad.children + ' of 5 components');
              }
              return cart;
            })
            .then(function (cart) {
              /*
                ============================================================
                THE SCREEN AND THE SERVER MUST AGREE BEFORE WE HAND OVER

                MEASURED, 2026-09-15: drawer open on a four pair cart showing
                GBP 150.00; something outside the drawer removed one glove,
                which leaves the tier's own BXGY unsatisfied and every glove
                back at full price. The server then held GBP 323.97 while the
                drawer still showed GBP 150.00, and CHECKOUT WENT STRAIGHT
                THROUGH. The shopper would have been charged GBP 173.97 more
                than the number they were looking at.

                It is stale by design and that part is right: an unsettled
                glove state is a half-finished tier, and painting it flashes
                nonsense mid-transition, so render() holds the last settled
                snapshot. What was missing is that HOLDING A STALE NUMBER IS
                ONLY SAFE WHILE NOBODY IS SPENDING MONEY.

                The trigger is any cart write that touches gloves and does
                not come from this drawer: a second tab, the back button, or
                another app. Kaching Bundles is live in the target
                architecture and works on exactly these lines, so this is not
                hypothetical.

                So the last thing before navigation is a comparison against
                the authoritative cart we have just read. On a mismatch the
                handover is refused, the TRUE total is painted - overriding
                the settled-snapshot hold, because at this moment honesty
                beats smoothness - and the shopper is told. Their next tap
                sees agreement and proceeds.

                It refuses the navigation; it does not strand anyone. Nothing
                is repaired here either: a write at the checkout barrier is
                exactly what the barrier exists to prevent.
                ============================================================
              */
              if (paintedSubtotal !== null && cart.total_price !== paintedSubtotal) {
                var shown = paintedSubtotal;
                forceTruthfulTotal = true;
                try { render(cart); } finally { forceTruthfulTotal = false; }
                var mismatch = new Error('subtotal mismatch: showing ' + shown +
                  ', server holds ' + cart.total_price);
                mismatch.hcdCartChanged = true;
                throw mismatch;
              }
              return cart;
            })
            .then(function (cart) {
              track('hundred_cart_checkout_click', {
                cart_value: cart.total_price, currency: cart.currency,
                protection_intent: protectionWanted() ? 'on' : 'off'
              });
              /*
                STRAIGHT TO CHECKOUT.

                This used to call checkoutForm.submit(). A PROGRAMMATIC form
                submit does not include the submitting button's name/value,
                so Shopify received POST /cart with no `checkout` parameter,
                treated it as an ordinary cart update and redirected to the
                /cart page. That was the extra cart page: the button is fine
                when a human clicks it, and the guard broke it by submitting
                in code.

                Navigating to /checkout goes to Shopify's hosted checkout
                directly, with no intermediate page and no second click.
              */
              window.location.href = '/checkout';
            })
            .catch(function (err) {
              try { console.warn('[hundred-cart] checkout blocked', err); } catch (x) {}
              release(err && err.hcdCartChanged
                ? 'YOUR CART CHANGED. CHECK THE TOTAL, THEN TAP CHECKOUT AGAIN.'
                : 'Something went wrong. Please try checkout again.');
            });
        })();
      }, true);
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-hcd-close]'), function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || root.hidden) return;
      if (!sheetEl.hidden) { closeSheet(); return; }
      close();
    });

    listEl.addEventListener('click', function (e) {
      var host = e.target.closest('[data-line]');
      if (!host) return;
      var key = host.getAttribute('data-line');

      var qty = e.target.closest('[data-hcd-qty]');
      if (qty) {
        var delta = parseInt(qty.getAttribute('data-hcd-qty'), 10);
        var cur = parseInt(host.querySelector('.hcd__qty span').textContent, 10);
        return change(key, Math.max(0, cur + delta));
      }
      if (e.target.closest('[data-hcd-remove]')) return change(key, 0);
    });

    /* While the flag is on, the drawer takes over every cart trigger on the
       page, including the one Kaching listens to, so the two can never both
       open. Kaching itself is untouched and still owns the live cart for
       everyone without the flag.

       Bound to WINDOW, not document, and deliberately so. Kaching's embed
       loads in the head and registers its own capture listener on document
       before this file has even parsed, so a document-level listener here
       fires second and never sees the click. Capture runs window first, so
       this is the only place the interception is reliable. Measured: on
       document the drawer never opened. */
    window.addEventListener('click', function (e) {
      var t = e.target.closest('a[href$="/cart"], a[href*="/cart?"], [data-hcd-open], cart-drawer-component button');
      if (!t) return;
      e.preventDefault();
      e.stopPropagation();
      open();
    }, true);

    /*
      ============================================================
      THE THEME'S CART OPENS INSTEAD OF OURS AFTER THE OCU UPSELL

      Reported as "the other cart". It is not Kaching - that embed is off and
      absent from the page. It is Horizon's own <dialog class="cart-drawer__
      dialog">, reached like this:

        PDP Add to Cart -> Zipify OCU upsell -> decline -> theme drawer opens

      Our drawer deliberately does NOT open while OCU owns the flow: opening
      on a cart OCU has not finished writing drew "Your cart is empty". So the
      theme's `auto-open` wins the moment OCU releases, and the customer ends
      up in a cart that is not ours.

      CSS hides the panel. This closes it as well, because the dialog carries
      `scroll-lock` and a hidden-but-open modal would leave the page locked,
      and then opens ours in its place - so that path ends in one cart, ours.
      The 44x44 header trigger is untouched; its click is already intercepted.
      ============================================================
    */
    (function adoptThemeCart() {
      var dlg = document.querySelector('.cart-drawer__dialog');
      if (!dlg) return;
      var takeOver = function () {
        if (!dlg.open) return;
        try { dlg.close(); } catch (e) {}
        /* Whatever just added to the cart, show the customer ours. */
        if (root && root.hidden) open();
        else paintThenSettle(fetchCart());
      };
      try {
        new MutationObserver(takeOver)
          .observe(dlg, { attributes: true, attributeFilter: ['open'] });
      } catch (e) {}
      takeOver();
    })();

    fetchCart().then(render);
    root.setAttribute('data-hcd-ready', '');

    /* A named API rather than a DOM poke, mirroring the shape Kaching
       exposes. Used by the QA harness now and by the cutover later. */
    /*
      ONE open path. Any successful non-Academy add dispatches
      'hundred-cart:open' and this listens; nothing animates its own drawer.

      ACADEMY IS DELIBERATELY EXCLUDED. Zipify OCU intercepts the Academy
      form submit before it reaches /cart/add.js and owns the flow from
      there. Opening on that path would either fight OCU for the screen or
      open on a cart that OCU has not finished writing. So this listens for
      /cart/add.js responses only, which is precisely the set of adds OCU did
      NOT intercept: if OCU took it, no request is seen and nothing opens; if
      it did not, the add completes normally and the cart opens.
    */
    document.addEventListener('hundred-cart:open', function () { open(); });

    if (window.fetch && !window.__hundredCartHooked) {
      window.__hundredCartHooked = true;
      var nativeFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        return nativeFetch.apply(this, arguments).then(function (res) {
          try {
            /*
              ============================================================
              AN EXTERNAL WRITE IS READ AND RENDERED. IT IS NOT RECONCILED.

              Only /cart/add.js was watched, so an external REMOVE or UPDATE
              went unnoticed and the drawer kept showing money the cart no
              longer held. Measured: server £60.99 against £82.98 on screen,
              and a healthy £132.99 Academy on screen against £283.95 held.

              The response to that is deliberately narrow. Every observed
              request used to start paintThenSettle, which reconciles, which
              writes, which another actor reacts to - the feedback loop that
              cost 40 requests on an Academy cart. So an external write earns
              exactly ONE authoritative read and ONE render. No reconcile, no
              write. If the newest cart genuinely breaks an invariant, the
              integrity check reports it and checkout refuses; it is not
              repaired from here.

              Bursts coalesce: several external writes in flight collapse into
              the single read at the end of the window.
              ============================================================
            */
            if (/\/cart\/(change|update)(\.js)?/.test(url) && res.ok &&
                internalWrite === 0 && root && !root.hidden) {
              clearTimeout(externalSyncTimer);
              externalSyncTimer = setTimeout(function () {
                if (backgroundWritesBlocked()) return;   /* checkout owns the cart */
                if (internalWrite > 0) return;           /* ours after all */
                fetchCart().then(render).catch(function () {});
              }, 300);
            }
            if (/\/cart\/add(\.js)?/.test(url) && res.ok) {
              /*
                ============================================================
                THE ONE MOMENT PROTECTION MAY DEFAULT ITSELF ON

                Promotion happens HERE - after a successful /cart/add.js that
                carried real merchandise - and nowhere else. Not on drawer
                open, not in reconcile, not on a cart read. That is the whole
                point: UNSET stays UNSET until the shopper actually buys
                something, so a cart built before this release never gains
                GBP 3 from being looked at.

                AFTER the response, never before: a 422 or 500 leaves intent
                UNSET, so a failed first add cannot silently opt someone in.

                addIsCustomerMerchandise() reads the request body, so the
                protection line adding itself and reconcile's gift add are
                both excluded by identity rather than by timing - which is
                what prevents promote -> add protection -> promote.

                An explicit 'off' is never touched. Only absent becomes 'on'.
                ============================================================
              */
              try {
                if (getProtectionIntent() === 'unset' && addIsCustomerMerchandise(init)) {
                  setProtectionWanted(true);
                }
              } catch (e) {}
              /*
                Let Shopify and any other listener settle first, then take ONE
                of two paths.

                A single Add to Cart produces SEVERAL /cart/add.js calls on
                this store: Kaching writes its protection line and OCU makes
                its own calls around the shopper's. Measured: three on a plain
                glove add. Opening on the first one caught the cart before the
                shopper's item had landed and drew "Your cart is empty".

                So if the drawer is already open, this refreshes it in place
                instead of opening it again. The panel does not re-enter, the
                animation does not replay, and the later calls in the same
                burst are what put the item on screen.
              */
              if (internalAdd > 0) return res;
              /*
                NOT DEBOUNCED, AND THAT WAS MEASURED.

                A 450ms coalescing window was tried here on the theory that
                bursts of Kaching writes were each starting a lifecycle. It
                made things worse, not better: Academy went from 27 requests
                to 46 and protection from 21 to 29. Delaying our settle gives
                Kaching a LARGER window to write into before we reconcile, so
                reconcile then has more to correct and provokes more of its
                responses in turn. Reverted.
              */
              setTimeout(function () {
                if (!root) return;
                if (backgroundWritesBlocked()) return;   /* checkout owns the cart */
                if (root.hidden) {
                  document.dispatchEvent(new CustomEvent('hundred-cart:open'));
                  return;
                }
                /* Already settling: it will observe this change itself. */
                if (settling) { dirty = true; return; }
                paintThenSettle(fetchCart());
              }, 250);
            }
          } catch (e) {}
          return res;
        });
      };
    }

    window.HundredCart = {
      open: open,
      close: close,
      refresh: function () { return fetchCart().then(render); },
      isOpen: function () { return !root.hidden; },

      /*
        READ-ONLY DIAGNOSTIC. Runs the live recommendation engine against a
        cart object without touching the real cart, so coverage can be proved
        across the whole catalogue without forty write cycles against the
        storefront - which is what tripped the bot protection last time.

        It is the SAME code path the drawer renders from. A harness that
        reimplemented the ladder would only ever prove itself right.
      */
      evaluate: function (cart) {
        cart = cart || lastCart || { items: [], item_count: 0, currency: 'GBP', total_price: 0 };
        if (!cart.item_count) cart.item_count = (cart.items || []).length;
        var ladder = ladderOffer(cart);
        if (ladder) {
          return {
            kind: 'glove-tier',
            handle: 'glove-tier-' + ladder.tier.qty,
            title: 'Add a pair, ' + ladder.tier.qty + ' pairs for ' + ladder.tier.price,
            tier: ladder.tier.qty,
            incremental: ladder.incremental,
            count: 1
          };
        }
        if (qualifyingMerchandiseSubtotal(cart) <= 0) {
          return { kind: 'none', handle: null, count: 0 };
        }
        var u = pickUpsell(cart);
        if (!u) return { kind: 'category-cta', handle: null, count: 1 };
        var v = upsellVariant(u);
        return {
          kind: 'product',
          handle: u.handle,
          product_id: u.product_id,
          title: u.title,
          cat: u.cat,
          price: u.price,
          sized: !!u.sized,
          variants: (u.variants || []).length,
          ready: !!v,
          hook: (upsellHook(cart, u, v ? v.price : u.price) || {}).text || null,
          count: 1
        };
      },
      pool: function () { return upsellCandidates(); }
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
